/**
 * @file Core blocking engine for GDPR-compliant consent management.
 * @summary Proactive blocking system that intercepts browser APIs to enforce
 * user consent choices before tracking scripts can execute or collect data.
 *
 * This module implements comprehensive privacy protection using multi-layered
 * classification, native API interception, real-time DOM monitoring, and robust
 * proxy detection to prevent consent circumvention. Designed for CDN deployment
 * with minimal performance impact whilst ensuring no tracking occurs without consent.
 *
 * @author University College London
 * @version 3.3.4
 * @since 1.0.0
 * @requires ES6+ (Map, WeakSet, URL, MutationObserver)
 * @license MIT
 */

/*
 * Import domain classification data from external JSON files.
 * Modular structure enables security teams to update threat intelligence
 * independently from application deployment cycles.
 */
import necessaryDomains from './domains/necessary.json';      // Essential functionality domains.
import analyticsDomains from './domains/analytics.json';      // Analytics and performance tracking.
import marketingDomains from './domains/marketing.json';      // Advertising and marketing platforms.
import embedsDomains from './domains/embeds.json';            // Third-party embedded content services.
import domainPatterns from './domains/patterns.json';         // URL and content classification patterns.
import proxyPatterns from './domains/proxy-patterns.json';    // oEmbed and CMS proxy detection patterns.
import cookiePatterns from './domains/cookie-patterns.json';  // Cookie name classification patterns.

(function() {
    'use strict';

    /*
     * Preserve native browser APIs before interception.
     * Essential for security and functionality — allows clean restoration and
     * prevents infinite recursion when our overrides need to call the originals.
     */
    const originalMethods = {
        xmlHttpRequestOpen: XMLHttpRequest.prototype.open,
        fetch: window.fetch,
        createElement: document.createElement,
        cookieSetter: Object.getOwnPropertyDescriptor(Document.prototype, 'cookie')?.set,
        cookieGetter: Object.getOwnPropertyDescriptor(Document.prototype, 'cookie')?.get
    };

    /**
     * Validates and sanitises user configuration to prevent injection attacks.
     * Uses allowlist validation rather than blocklist filtering to ensure only
     * expected properties with correct types are processed. This comprehensive
     * approach protects against malicious configuration that could bypass security
     * controls or exploit vulnerabilities in the consent manager.
     *
     * The validation includes numeric domain rejection, URL protocol verification,
     * HTML entity encoding, and strict type checking to maintain system integrity.
     * New extension capabilities support cookiePatterns and platformMapping while
     * maintaining security boundaries through rigorous validation.
     *
     * Security considerations:
     * - Pattern strings validated for regex injection attacks
     * - Platform names sanitised against XSS vulnerabilities
     * - Configuration size limits prevent DoS attacks
     * - Type coercion blocked to prevent property pollution
     *
     * @param {object} userConfig - Raw configuration from window.privacyConfig.
     * @returns {object} Sanitised configuration safe for internal use.
     */
    function validateConfig(userConfig) {
        if (!userConfig || typeof userConfig !== 'object') {
            return {};
        }
        const validated = {};
        if (userConfig.reclassify && typeof userConfig.reclassify === 'object') {
            validated.reclassify = {};
            Object.keys(userConfig.reclassify).forEach(domain => {
                const purpose = userConfig.reclassify[domain];
                if (typeof domain === 'string' && domain.length > 0 && !/^\d+$/.test(domain) && typeof purpose === 'string' && ['necessary', 'analytics', 'marketing', 'embeds'].includes(purpose)) {  // Reject numeric-only domains to prevent IP address configuration abuse.
                    validated.reclassify[domain] = purpose;
                }
            });
        }
        if (userConfig.extend && typeof userConfig.extend === 'object') {
            validated.extend = {};
            Object.entries(userConfig.extend).forEach(([purpose, domains]) => {
                if (['analytics', 'marketing', 'embeds'].includes(purpose) && Array.isArray(domains)) {
                    validated.extend[purpose] = domains.filter(d => typeof d === 'string');
                }
            });
        }
        if (userConfig.allowlist && Array.isArray(userConfig.allowlist)) {
            validated.allowlist = userConfig.allowlist.filter(item => typeof item === 'string');
        } else if (userConfig.whitelist && Array.isArray(userConfig.whitelist)) {
            // Backwards compatibility: 'whitelist' deprecated, use 'allowlist'.
            validated.allowlist = userConfig.whitelist.filter(item => typeof item === 'string');
        }
        if (userConfig.scriptPatterns && typeof userConfig.scriptPatterns === 'object') {
            validated.scriptPatterns = {};
            Object.entries(userConfig.scriptPatterns).forEach(([purpose, patterns]) => {
                if (['necessary', 'analytics', 'marketing', 'embeds'].includes(purpose) && Array.isArray(patterns)) {
                    validated.scriptPatterns[purpose] = patterns.filter(p => p && typeof p === 'object' && typeof p.pattern === 'string' && typeof p.service === 'string');
                }
            });
        }
        /*
         * Cookie pattern extension validation with regex injection prevention.
         * Allows site operators to extend cookie classification with custom patterns
         * whilst preventing regex injection attacks through comprehensive validation.
         * Patterns are tested for compilation safety before acceptance.
         */
        if (userConfig.cookiePatterns && typeof userConfig.cookiePatterns === 'object') {
            validated.cookiePatterns = {};
            Object.entries(userConfig.cookiePatterns).forEach(([purpose, patterns]) => {
                if (['necessary', 'analytics', 'marketing', 'embeds'].includes(purpose) && Array.isArray(patterns)) {
                    validated.cookiePatterns[purpose] = patterns.filter(pattern => {
                        if (typeof pattern !== 'string' || pattern.length === 0 || pattern.length > 200) {
                            return false;  // DoS prevention: reject oversized patterns.
                        }
                        try {
                            new RegExp(pattern, 'i');  // Test compilation prevents regex injection.
                            return true;
                        } catch (e) {
                            if (config.options.debug) console.warn('UCL Privacy: Invalid cookie pattern rejected:', pattern);
                            return false;
                        }
                    });
                }
            });
        }
        /*
         * Platform mapping extension with XSS prevention and size limits.
         * Enables custom display names for embedded services whilst preventing
         * injection attacks through comprehensive input sanitisation. Domain keys
         * validated against malicious patterns and display names sanitised.
         */
        if (userConfig.platformMapping && typeof userConfig.platformMapping === 'object') {
            validated.platformMapping = {};
            Object.entries(userConfig.platformMapping).forEach(([domain, name]) => {
                if (typeof domain === 'string' && typeof name === 'string' &&
                    domain.length > 0 && domain.length <= 100 &&
                    name.length > 0 && name.length <= 50) {

                    const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                    if (domainPattern.test(domain)) {
                        // Sanitise against XSS whilst preserving readability.
                        const sanitisedName = name.replace(/[<>"'&]/g, '').trim();
                        if (sanitisedName.length > 0) {
                            validated.platformMapping[domain] = sanitisedName;
                        }
                    }
                }
            });
        }
        if (userConfig.options && typeof userConfig.options === 'object') {
            validated.options = {};
            const booleanOptions = ['logging', 'debug', 'enablePlaceholders', 'enableAnalytics', 'showBanner', 'blockFirstPartyInlineScripts', 'blockFirstPartyProxies', 'enableCookieCleanup'];
            booleanOptions.forEach(option => {
                if (typeof userConfig.options[option] === 'boolean') {
                    validated.options[option] = userConfig.options[option];
                }
            });
            if (typeof userConfig.options.blockingMode === 'string' && ['strict', 'balanced'].includes(userConfig.options.blockingMode)) {
                validated.options.blockingMode = userConfig.options.blockingMode;
            }
            if (typeof userConfig.options.defaultUnknownAction === 'string' && ['block', 'allow'].includes(userConfig.options.defaultUnknownAction)) {
                validated.options.defaultUnknownAction = userConfig.options.defaultUnknownAction;
            }
            if (typeof userConfig.options.bannerDelay === 'number' && userConfig.options.bannerDelay >= 0 && userConfig.options.bannerDelay <= 10000) {
                validated.options.bannerDelay = userConfig.options.bannerDelay;
            }
        }

        /*
         * Single sanitisation function optimised for performance and security.
         * Comprehensive HTML entity encoding prevents XSS attacks via configuration
         * injection whilst maintaining text readability for legitimate use cases.
         */
        const sanitizeText = (text) => {
            if (typeof text !== 'string') return '';
            if (text.length > 10000) return '';  // DoS prevention: reject oversized strings.

            /*
             * Comprehensive HTML entity encoding prevents XSS via configuration.
             * Uses numeric entities for maximum compatibility across different
             * rendering contexts. Covers all dangerous characters that could
             * potentially escape HTML attributes or JavaScript string contexts.
             */
            return text.trim()
                .replace(/[<>"'&`{}()[\]\\/]/g, (match) => {
                    const entities = {
                        '<': '&lt;',
                        '>': '&gt;',
                        '"': '&quot;',
                        "'": '&#x27;',
                        '&': '&amp;',
                        '`': '&#x60;',
                        '{': '&#x7B;',
                        '}': '&#x7D;',
                        '(': '&#x28;',
                        ')': '&#x29;',
                        '[': '&#x5B;',
                        ']': '&#x5D;',
                        '\\': '&#x5C;',
                        '/': '&#x2F;'
                    };
                    return entities[match];
                })
                .replace(/\s+/g, ' ');
        };

        /**
         * Privacy policy URL validation with comprehensive security controls.
         *
         * GDPR Article 12 requires transparent access to privacy information.
         * This validation ensures privacy policy links are secure whilst allowing
         * flexible deployment across different site architectures.
         *
         * Security threats addressed:
         * - XSS via javascript: pseudo-protocols
         * - Data exfiltration via data: URIs
         * - Directory traversal attacks (../../../etc/passwd)
         * - Protocol confusion attacks
         * - Malformed URL exploitation
         *
         * @example Valid URLs:
         * - "/privacy-policy.html" (relative)
         * - "./legal/privacy.php" (relative with directory)
         * - "https://example.com/privacy" (absolute HTTPS)
         * - "../docs/privacy.pdf" (relative traversal, within limits)
         *
         * @example Invalid URLs:
         * - "javascript:alert('xss')" (dangerous protocol)
         * - "data:text/html,<script>..." (data URI)
         * - "../../../../etc/passwd" (excessive traversal)
         * - "file:///etc/passwd" (file system access)
         */
        if (userConfig.privacyPolicySection && typeof userConfig.privacyPolicySection === 'object') {
            try {
                validated.privacyPolicySection = {};

                /*
                 * URL validation follows defence-in-depth principle with multiple
                 * security layers. Each check targets specific attack vectors whilst
                 * maintaining usability for legitimate privacy policy links.
                 */
                if (typeof userConfig.privacyPolicySection.url === 'string') {
                    /*
                     * Length validation prevents DoS attacks via oversized URLs.
                     * RFC 2616 suggests 2048 characters as practical limit for
                     * browser compatibility across all major browsers.
                     */
                    if (userConfig.privacyPolicySection.url.length > 2048) {
                        throw new Error('URL exceeds maximum length');
                    }

                    const url = userConfig.privacyPolicySection.url.trim();
                    if (url.length > 0) {
                        /*
                         * Protocol validation prevents pseudo-protocol attacks.
                         * Explicitly blocks dangerous schemes before URL parsing
                         * to prevent browser-specific interpretation differences.
                         */
                        const protocolMatch = url.match(/^([a-z][a-z0-9+.-]*):\/\//i);
                        if (protocolMatch) {
                            const protocol = protocolMatch[1].toLowerCase();
                            const dangerousProtocols = ['javascript', 'data', 'vbscript', 'file', 'ftp'];
                            if (dangerousProtocols.includes(protocol)) {
                                throw new Error('Unsafe protocol detected');
                            }
                        }

                        try {
                            let urlObj;
                            /*
                             * Relative URL processing with enhanced security controls.
                             * Supports common web server directory structures whilst
                             * preventing directory traversal and injection attacks.
                             */
                            if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../') || !url.includes('://')) {

                                /*
                                 * Character validation prevents multiple attack vectors:
                                 * - Control characters (\x00-\x1F): null byte injection, CRLF injection
                                 * - Extended ASCII (\x7F-\x9F): encoding confusion attacks
                                 * - HTML/JS chars (<>"'`): XSS via attribute injection
                                 * - Backslash (\): Windows path confusion
                                 * - Percent (%): double-encoding attacks
                                 */
                                // eslint-disable-next-line no-control-regex
                                if (/[<>"'`\\%\x00-\x1F\x7F-\x9F]/.test(url)) {
                                    throw new Error('URL contains dangerous characters');
                                }

                                /*
                                 * Path traversal protection with pragmatic limit.
                                 * Three levels of "../" allow reasonable site structures
                                 * (e.g., "../../../legal/privacy.html" for deep nesting)
                                 * whilst preventing access to system directories.
                                 */
                                const pathTraversalMatches = url.match(/\.\.\//g);
                                if (pathTraversalMatches && pathTraversalMatches.length > 3) {
                                    throw new Error('Excessive path traversal detected');
                                }

                                /*
                                 * Pattern-based attack detection using targeted regexes.
                                 * Each pattern addresses specific exploit techniques
                                 * commonly used in web application attacks.
                                 */
                                const suspiciousPatterns = [
                                    /\0/,                           // Null byte injection (C-style string termination)
                                    /%00/i,                        // URL-encoded null bytes (bypass basic filters)
                                    /%2e%2e%2f/i,                  // URL-encoded "../" (traversal encoding)
                                    /\.{3,}/,                      // Multiple dots (parser confusion)
                                    /\/\//,                        // Double slashes (protocol confusion)
                                    /[;?&].*[<>"']/,              // Dangerous chars in query parameters
                                    /^\s*javascript:/i,            // JavaScript pseudo-protocol (with whitespace)
                                    /^\s*data:/i,                 // Data URI scheme (with whitespace)
                                    /^\s*vbscript:/i,             // VBScript pseudo-protocol (IE legacy)
                                    /\.(exe|bat|cmd|scr|pif|com)$/i // Executable extensions (download attacks)
                                ];

                                for (const pattern of suspiciousPatterns) {
                                    if (pattern.test(url)) {
                                        throw new Error('Suspicious URL pattern detected');
                                    }
                                }

                                /*
                                 * Structural validation ensures URL conforms to expected web patterns.
                                 * Regex components:
                                 * - ^(\.\/|\.\.\/|\/)? : Optional relative/absolute prefix
                                 * - ([a-zA-Z0-9._-]+\/)* : Directory segments (alphanumeric + safe chars)
                                 * - [a-zA-Z0-9._-]* : Filename (optional, alphanumeric + safe chars)
                                 * - (\.[a-zA-Z0-9]{1,10})? : File extension (1-10 chars, optional)
                                 * - (#[a-zA-Z0-9._-]*)? : Fragment identifier (optional)
                                 * - (\?[a-zA-Z0-9=&._-]*)? : Query string (limited chars, optional)
                                 */
                                const validStructure = /^(\.\/|\.\.\/|\/)?([a-zA-Z0-9._-]+\/)*[a-zA-Z0-9._-]*(\.[a-zA-Z0-9]{1,10})?(#[a-zA-Z0-9._-]*)?(\?[a-zA-Z0-9=&._-]*)?$/;
                                if (!validStructure.test(url)) {
                                    throw new Error('Invalid URL structure');
                                }

                                /*
                                 * Path segment length validation prevents filesystem attacks.
                                 * 255 characters aligns with most filesystem limitations,
                                 * preventing buffer overflow attacks in underlying systems.
                                 */
                                const pathSegments = url.split('/').filter(segment => segment.length > 0);
                                for (const segment of pathSegments) {
                                    if (segment.length > 255) {
                                        throw new Error('URL path segment too long');
                                    }
                                }

                                /*
                                 * System path protection prevents access to sensitive directories.
                                 * Covers common Unix/Linux system paths that should never be
                                 * accessible via web requests, even on misconfigured servers.
                                 */
                                const systemPaths = ['/etc/', '/proc/', '/sys/', '/dev/', '/var/', '/tmp/', '/root/'];
                                const normalizedUrl = url.toLowerCase();
                                for (const systemPath of systemPaths) {
                                    if (normalizedUrl.includes(systemPath)) {
                                        throw new Error('Access to system paths not permitted');
                                    }
                                }

                                validated.privacyPolicySection.url = url;
                            } else {
                                /*
                                 * Absolute URL validation using native URL constructor.
                                 * Leverages browser's built-in parsing for comprehensive
                                 * validation whilst restricting to safe HTTP protocols.
                                 */
                                urlObj = new URL(url);
                                const normalizedProtocol = urlObj.protocol.toLowerCase();

                                /*
                                 * Protocol restriction to HTTP/HTTPS only.
                                 * GDPR requires accessible privacy policies; restricting
                                 * to web protocols ensures universal browser compatibility.
                                 */
                                if (normalizedProtocol === 'https:' || normalizedProtocol === 'http:') {
                                    validated.privacyPolicySection.url = url;
                                } else {
                                    throw new Error('Invalid protocol');
                                }
                            }
                        } catch (urlError) {
                            throw new Error('Invalid URL format');
                        }
                    } else {
                        throw new Error('Empty URL');
                    }
                } else {
                    throw new Error('URL is required');
                }

                // Only proceed if we have a valid URL.
                if (validated.privacyPolicySection.url) {
                    // Validate linkText (optional, default: "Privacy Policy").
                    if (typeof userConfig.privacyPolicySection.linkText === 'string' &&
                        userConfig.privacyPolicySection.linkText.length <= 100) {
                        const linkText = sanitizeText(userConfig.privacyPolicySection.linkText);
                        // Handle empty strings after sanitisation.
                        validated.privacyPolicySection.linkText = (linkText && linkText.length > 0) ? linkText : 'Privacy Policy';
                    } else {
                        validated.privacyPolicySection.linkText = 'Privacy Policy';
                    }

                    // Validate title (optional, default: "Privacy Policy").
                    if (typeof userConfig.privacyPolicySection.title === 'string' &&
                        userConfig.privacyPolicySection.title.length <= 100) {
                        const title = sanitizeText(userConfig.privacyPolicySection.title);
                        // Handle empty strings after sanitisation.
                        validated.privacyPolicySection.title = (title && title.length > 0) ? title : 'Privacy Policy';
                    } else {
                        validated.privacyPolicySection.title = 'Privacy Policy';
                    }

                    // Validate description (optional, no default).
                    if (typeof userConfig.privacyPolicySection.description === 'string' &&
                        userConfig.privacyPolicySection.description.length <= 500) {
                        const description = sanitizeText(userConfig.privacyPolicySection.description);
                        if (description && description.length > 0) {
                            validated.privacyPolicySection.description = description;
                        }
                    }

                    // Validate enabled flag (optional, default: true).
                    validated.privacyPolicySection.enabled =
                        typeof userConfig.privacyPolicySection.enabled === 'boolean'
                            ? userConfig.privacyPolicySection.enabled
                            : true;
                } else {
                    // Remove privacyPolicySection if URL is invalid.
                    delete validated.privacyPolicySection;
                }
            } catch (error) {
                // Proper error handling without exposing internals.
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn('UCL Privacy: Invalid privacy policy configuration - ignoring section');
                }
                // Remove invalid configuration entirely.
                delete validated.privacyPolicySection;
            }
        }

        return validated;
    }

    const userConfig = validateConfig(window.privacyConfig || {});

    if (window.privacyConfig && window.privacyConfig.options && window.privacyConfig.options.debug) {
        console.log('UCL Privacy: Configuration validated', { original: window.privacyConfig, validated: userConfig });
    }

    /*
     * Classification database for automated consent enforcement.
     * Maintains curated lists of tracking domains, URL patterns, and cookie
     * signatures organised by purpose category. This database is regularly
     * updated to maintain effectiveness against evolving tracking techniques
     * and emerging privacy threats.
     *
     * The multi-layered classification approach combines:
     * 1. Exact domain matching (fastest, O(1) lookup)
     * 2. Subdomain pattern matching for service variants
     * 3. URL regex analysis for behavioural classification
     * 4. Content pattern matching for inline scripts
     */
    const config = {
        /*
         * Core domain classifications for major tracking services.
         * Loaded from external JSON files to enable easier maintenance
         * and updates without code changes. Hash map structure provides
         * O(1) lookup performance for exact domain matches.
         */
        coreDomains: {
            necessary: necessaryDomains,
            analytics: analyticsDomains,
            marketing: marketingDomains,
            embeds: embedsDomains
        },
        /*
         * Domain suffix patterns for dynamic classification.
         * Catches tracking services that use predictable naming conventions
         * such as 'analytics.example.com', 'ads.platform.net', or
         * 'tracking.service.io'. Essential for new services not yet in core lists.
         */
        domainSuffixes: domainPatterns.domainSuffixes,
        /*
         * URL pattern matching for script and request classification.
         * Regex patterns detect tracking behaviour in URLs, query parameters,
         * and path structures. Patterns are ordered by frequency for performance
         * optimisation, with most common tracking patterns evaluated first.
         */
        patterns: (() => {
            // Convert regex pattern strings back to RegExp objects
            const patterns = {};
            Object.entries(domainPatterns.urlPatterns).forEach(([purpose, patternStrings]) => {
                patterns[purpose] = patternStrings.map(patternStr => {
                    // Extract regex pattern and flags from JSON string format like "/pattern/flags".
                    const match = patternStr.match(/^\/(.*)\/([gimuy]*)$/);
                    if (match) {
                        return new RegExp(match[1], match[2]);
                    }
                    // Fallback for simple string patterns — convert to case-insensitive regex.
                    return new RegExp(patternStr, 'i');
                });
            });
            return patterns;
        })(),
        /*
         * Inline script content classification patterns.
         * Identifies tracking code by analysing function calls, API usage,
         * and distinctive code signatures. High-confidence patterns ensure
         * accurate categorisation whilst minimising false positives.
         */
        scriptPatterns: domainPatterns.scriptPatterns,
        /*
         * Cookie name patterns for real-time classification.
         * Imported from external JSON file for maintainability and version control.
         * Based on documented naming conventions from major analytics and advertising
         * platforms. Patterns converted from strings to RegExp objects for performance.
         */
        cookiePatterns: (() => {
            const patterns = {};
            Object.entries(cookiePatterns).forEach(([purpose, patternStrings]) => {
                patterns[purpose] = patternStrings.map(pattern => new RegExp(pattern, 'i'));
            });
            return patterns;
        })(),
        /*
         * Default operational settings optimised for GDPR compliance.
         * 'strict' mode prioritises user privacy by erring on the side of blocking.
         * 500ms banner delay provides users time to read content before consent prompt,
         * preventing accidental clicks and ensuring informed decision-making.
         */
        options: { logging: false, debug: false, blockingMode: 'strict', defaultUnknownAction: 'block', enablePlaceholders: true, enableAnalytics: true, showBanner: true, bannerDelay: 500, blockFirstPartyInlineScripts: false, blockFirstPartyProxies: true, enableCookieCleanup: true }
    };

    /**
     * Merges validated user configuration with secure defaults.
     * Allows site-specific customisation whilst maintaining security by processing
     * only pre-validated configuration objects. Supports domain reclassification
     * and category extension without compromising baseline protection mechanisms.
     *
     * This approach enables flexible consent management whilst preventing
     * configuration-based security bypasses or privacy violations.
     */
    function mergeConfiguration() {
        if (userConfig.reclassify) {
            Object.entries(userConfig.reclassify).forEach(([domain, purpose]) => {
                /*
                 * Clean removal prevents domains from being classified in multiple
                 * categories simultaneously, which could lead to inconsistent blocking
                 * behaviour and confusion about consent requirements.
                 */
                Object.keys(config.coreDomains).forEach(cat => {
                    const index = config.coreDomains[cat].indexOf(domain);
                    if (index > -1) config.coreDomains[cat].splice(index, 1);
                });
                if (config.coreDomains[purpose]) config.coreDomains[purpose].push(domain);
            });
        }
        if (userConfig.extend) {
            Object.entries(userConfig.extend).forEach(([purpose, domains]) => {
                if (config.coreDomains[purpose]) config.coreDomains[purpose].push(...domains);
            });
        }
        if (userConfig.scriptPatterns) {
            Object.entries(userConfig.scriptPatterns).forEach(([purpose, patterns]) => {
                if (config.scriptPatterns[purpose]) config.scriptPatterns[purpose].push(...patterns);
            });
        }
        /*
         * Cookie pattern extension integration with performance optimisation.
         * Converts validated string patterns to compiled RegExp objects for runtime
         * efficiency. Pre-compilation prevents regex compilation overhead during
         * real-time cookie classification operations.
         */
        if (userConfig.cookiePatterns) {
            Object.entries(userConfig.cookiePatterns).forEach(([purpose, patterns]) => {
                if (config.cookiePatterns[purpose]) {
                    const regexPatterns = patterns.map(pattern => new RegExp(pattern, 'i'));
                    config.cookiePatterns[purpose].push(...regexPatterns);
                    if (config.options.debug) {
                        console.log(`UCL Privacy: Extended ${purpose} cookie patterns:`, patterns);
                    }
                }
            });
        }
        if (userConfig.options) {
            Object.assign(config.options, userConfig.options);
        }
        if (userConfig.allowlist) {
            config.allowlist = userConfig.allowlist;
        } else if (userConfig.whitelist) {
            // Fallback to whitelist for backwards compatibility.
            config.allowlist = userConfig.whitelist;
        }

        /*
         * Privacy policy section integration for regulatory compliance.
         * Required for transparency obligations under GDPR Articles 13 and 14,
         * which mandate clear information about data processing purposes.
         */
        if (userConfig.privacyPolicySection) {
            config.privacyPolicySection = userConfig.privacyPolicySection;
        }
    }
    mergeConfiguration();

    const allowlist = config.allowlist || [];

    /*
     * Performance caches for classification operations.
     * Domain lookup map provides O(1) average time complexity for domain classification,
     * significantly faster than array iteration. URL pattern cache prevents repeated
     * regex evaluation on the same URLs, reducing CPU overhead during classification.
     */
    const domainLookupMap = new Map();
    const urlPatternCache = new Map();
    const allowlistCache = new Map();

    /**
     * Builds optimised domain lookup map for O(1) classification performance.
     * Pre-processes domain variants including www. prefixes and subdomains to ensure
     * comprehensive matching without repetitive array iterations during real-time
     * request processing. Critical for maintaining page performance under load.
     *
     * Uses normalised domain names as keys to prevent classification inconsistencies
     * caused by URL formatting variations. Handles protocol-relative URLs, paths,
     * ports, wildcards, and FQDN trailing dots for robust domain matching.
     *
     * @returns {void} Modifies domainLookupMap global cache in-place.
     */
    function buildDomainLookupMap() {
        domainLookupMap.clear();

        const normalizeDomain = (input) => {
            if (!input && input !== 0) return '';
            let d = String(input).trim();

            // Handle protocol-relative: //example.com.
            if (d.startsWith('//')) {
                d = 'https:' + d;
            }

            // If input looks like a full URL with protocol, parse it properly.
            if (d.includes('://')) {
                try {
                    d = new URL(d).hostname;
                } catch (e) {
                    // Fall through to manual cleanup if URL parsing fails due to malformed input.
                }
            }

            // Strip path segments and ports to isolate hostname for classification.
            // Examples: "google.com/pagead" → "google.com", "example.com:8443" → "example.com".
            d = d.split('/')[0] || '';
            d = d.split(':')[0] || '';

            // Remove wildcard prefixes and leading dots from domain specifications.
            // Examples: "*.example.com" → "example.com", ".example.com" → "example.com".
            d = d.replace(/^\*\.\s*/, '').replace(/^\./, '');

            // Remove trailing dots from fully-qualified domain names (FQDNs).
            d = d.replace(/\.$/, '');

            // Convert to lowercase for case-insensitive domain matching.
            d = d.toLowerCase();

            // Basic validation: domain must contain at least one dot and valid labels.
            if (!d || !d.includes('.')) return '';

            return d;
        };

        Object.entries(config.coreDomains).forEach(([purpose, domains]) => {
            domains.forEach((domain) => {
                const cleanDomain = normalizeDomain(domain);
                if (!cleanDomain) return;  // Skip invalid or malformed domain entries.

                domainLookupMap.set(cleanDomain, purpose);

                // Also map the non-www variant for comprehensive subdomain coverage.
                if (cleanDomain.startsWith('www.')) {
                    domainLookupMap.set(cleanDomain.substring(4), purpose);
                }
            });
        });
    }
    buildDomainLookupMap();

    /*
     * Performance monitoring utility for development debugging.
     * Measures execution time of classification operations to identify performance
     * bottlenecks and optimisation opportunities. Only active when debug is
     * enabled to avoid production overhead and potential timing attacks.
     */
    const performanceMonitor = {
        times: new Map(),
        start(label) { this.times.set(label, performance.now()); },
        end(label) {
            const startTime = this.times.get(label);
            if (startTime) {
                const duration = performance.now() - startTime;
                if (config.options.debug) console.log(`UCL Privacy ${label}: ${duration.toFixed(2)}ms`);
                this.times.delete(label);
                return duration;
            }
            return 0;
        }
    };

    /*
     * Statistics collection for blocked resource monitoring.
     * Tracks blocking metrics by resource type and purpose category to provide
     * visibility into consent manager effectiveness, compliance monitoring,
     * and troubleshooting insights for site administrators.
     */
    const stats = {
        blocked: { scripts: 0, requests: 0, cookies: 0, iframes: 0 },
        byPurpose: { necessary: { scripts: 0, requests: 0, cookies: 0, iframes: 0 }, analytics: { scripts: 0, requests: 0, cookies: 0, iframes: 0 }, marketing: { scripts: 0, requests: 0, cookies: 0, iframes: 0 }, embeds: { scripts: 0, requests: 0, cookies: 0, iframes: 0 }, unknown: { scripts: 0, requests: 0, cookies: 0, iframes: 0 } }
    };

    // Consent state defaults to necessary-only for privacy-first operation.
    const consentState = { necessary: true, analytics: false, marketing: false, embeds: false };

    // Consent state tracking enables smart reprocessing optimisations.
    let lastConsentState = null;

    // User consent decision flag controls banner display logic.
    let hasConsented = false;

    /**
     * Loads user consent preferences from localStorage with integrity validation.
     * Enables consent persistence across browsing sessions whilst handling storage
     * errors gracefully. Validates stored data structure to prevent corruption-based
     * bypasses or unexpected application behaviour.
     *
     * Performs schema validation to ensure all required consent categories are present
     * with boolean values. Returns null for missing, corrupted, or invalid data to
     * trigger safe default behaviour.
     *
     * @returns {object|null} Validated consent object with boolean categories or null if invalid.
     * @example
     * // Valid return format:
     * { necessary: true, analytics: false, marketing: false, embeds: true }
     */
    function loadStoredConsent() {
        try {
            const stored = localStorage.getItem('ucl_privacy_consent');
            if (stored) {
                const parsed = JSON.parse(stored);
                // Schema validation ensures stored object matches expected consent structure.
                if (parsed && typeof parsed === 'object' && 'necessary' in parsed && 'analytics' in parsed && 'marketing' in parsed && 'embeds' in parsed) {
                    return parsed;
                }
            }
        } catch (error) {
            if (config.options.debug) console.warn('UCL Privacy: Error loading consent:', error.message);
        }
        return null;
    }

    /**
     * Persists user consent preferences to localStorage with error handling.
     * Provides atomic storage operation with validation to ensure data integrity.
     * Graceful failure handling prevents application crashes in restricted environments.
     *
     * Serialises consent state to JSON and stores under the key 'ucl_privacy_consent'.
     * Handles storage quota exceeded, disabled localStorage, and JSON serialisation errors.
     *
     * @param {object} consentData - Validated consent state object to persist.
     * @param {boolean} consentData.necessary - Essential functionality consent (always true).
     * @param {boolean} consentData.analytics - Analytics and performance tracking consent.
     * @param {boolean} consentData.marketing - Advertising and marketing consent.
     * @param {boolean} consentData.embeds - Third-party embedded content consent.
     * @returns {boolean} True on successful storage operation, false on any failure.
     * @throws {Error} Does not throw - all exceptions are caught and logged.
     */
    function saveConsent(consentData) {
        try {
            localStorage.setItem('ucl_privacy_consent', JSON.stringify(consentData));
            return true;
        } catch (error) {
            if (config.options.debug) console.warn('UCL Privacy: Error saving consent:', error.message);
            return false;
        }
    }

    /**
     * Initialises consent state by loading preferences from persistent storage.
     * Updates in-memory consent state with stored preferences whilst maintaining
     * backwards compatibility with legacy storage formats. Ensures consent history
     * is preserved across application updates and browser sessions.
     *
     * Attempts to load from 'ucl_privacy_consent' first, then falls back to legacy
     * 'ucl_privacy_has_consented' for backwards compatibility with older versions.
     * Updates global consentState and hasConsented flags based on stored data.
     *
     * @returns {void} Updates global state variables in-place.
     */
    function loadConsent() {
        const storedConsent = loadStoredConsent();
        if (storedConsent) {
            Object.assign(consentState, storedConsent);
            hasConsented = true;
        }
        // Backwards compatibility fallback for legacy storage format or data recovery.
        try {
            if (localStorage.getItem('ucl_privacy_has_consented') === 'true') hasConsented = true;
        } catch (error) {
            if (config.options.debug) console.warn('UCL Privacy: Error loading consent flag:', error.message);
        }
    }

    /*
     * Enhanced consent state management with atomic operations.
     * Prevents race conditions and ensures consistent state between storage,
     * memory, and UI components through proper sequencing and validation.
     */
    let consentUpdateInProgress = false;
    const consentUpdateQueue = [];

    /**
     * Thread-safe consent state persistence with proper error handling.
     * Coordinates storage persistence with event-driven UI updates to ensure
     * immediate reflection of user choices. Enhanced with atomic operations
     * and proper error recovery to prevent inconsistent states.
     */
    function saveUserConsent() {
        // Prevent concurrent consent updates that could cause race conditions.
        if (consentUpdateInProgress) {
            debug('Consent update already in progress, queueing operation');
            return new Promise((resolve) => {
                consentUpdateQueue.push(() => {
                    resolve(saveUserConsentImmediate());
                });
            });
        }

        return saveUserConsentImmediate();
    }

    /**
     * Immediate consent saving with atomic operations.
     * Performs the actual consent saving with proper state validation,
     * error handling, and event coordination.
     */
    function saveUserConsentImmediate() {
        consentUpdateInProgress = true;

        try {
            // Validate consent state structure before saving.
            const requiredKeys = ['necessary', 'analytics', 'marketing', 'embeds'];
            const isValidState = requiredKeys.every(key =>
                key in consentState && typeof consentState[key] === 'boolean'
            );

            if (!isValidState) {
                console.error('UCL Privacy: Invalid consent state structure, aborting save:', consentState);
                throw new Error('Invalid consent state structure');
            }

            // Create a snapshot of current state for atomic operation.
            const stateSnapshot = { ...consentState };
            const previousConsented = hasConsented;

            // Attempt to save consent data atomically.
            const success = saveConsent(stateSnapshot);

            if (success) {
                try {
                    localStorage.setItem('ucl_privacy_has_consented', 'true');
                    hasConsented = true;

                    // Dispatch event only after successful storage.
                    window.dispatchEvent(new CustomEvent('ucl-privacy-consent-updated', {
                        detail: {
                            state: stateSnapshot,
                            previousState: previousConsented ? loadStoredConsent() : null,
                            timestamp: Date.now()
                        }
                    }));

                    if (config.options.debug) {
                        console.log('UCL Privacy: Consent state saved successfully:', stateSnapshot);
                    }

                    return true;
                } catch (flagError) {
                    console.warn('UCL Privacy: Error saving consent flag, but data was saved:', flagError.message);

                    // Still dispatch event since data was saved successfully.
                    window.dispatchEvent(new CustomEvent('ucl-privacy-consent-updated', {
                        detail: {
                            state: stateSnapshot,
                            previousState: previousConsented ? loadStoredConsent() : null,
                            timestamp: Date.now(),
                            warning: 'Flag save failed but data persisted'
                        }
                    }));

                    return true;
                }
            } else {
                console.error('UCL Privacy: Failed to save consent state to storage');

                // Dispatch failure event for UI to handle.
                window.dispatchEvent(new CustomEvent('ucl-privacy-consent-save-failed', {
                    detail: {
                        state: stateSnapshot,
                        timestamp: Date.now(),
                        error: 'Storage operation failed'
                    }
                }));

                return false;
            }
        } catch (error) {
            console.error('UCL Privacy: Critical error during consent save:', error);

            // Dispatch critical error event.
            window.dispatchEvent(new CustomEvent('ucl-privacy-consent-save-failed', {
                detail: {
                    state: consentState,
                    timestamp: Date.now(),
                    error: error.message
                }
            }));

            return false;
        } finally {
            consentUpdateInProgress = false;

            // Process any queued consent updates.
            if (consentUpdateQueue.length > 0) {
                const nextUpdate = consentUpdateQueue.shift();
                // Use setTimeout to prevent stack overflow and allow current operation to complete.
                setTimeout(nextUpdate, 0);
            }
        }
    }

    loadConsent();
    // Initialise smart processing state tracking.
    lastConsentState = JSON.stringify(consentState);

    /**
     * Logs operational messages when logging is enabled.
     * Provides controlled visibility into blocking decisions and system behaviour
     * without compromising performance in production environments. Essential for
     * debugging consent issues and verifying compliance effectiveness.
     *
     * @param {string} message - Primary log message.
     * @param {...*} args - Additional context data for debugging.
     */
    function log(message, ...args) { if (config.options.logging || config.options.debug) console.log('[UCL Privacy]', message, ...args); }

    /**
     * Enhanced debug logging with categories and throttling.
     * Used for development troubleshooting and classification analysis.
     * Reduces console noise while maintaining useful debugging information.
     * @param {string} message - The message to log.
     * @param {...*} args - Additional arguments to log.
     */
    function debug(message, ...args) {
        if (config.options.debug) {
            // Group related debug messages to reduce console noise.
            const messageKey = message.split(' ')[0];
            const now = Date.now();

            // Throttle frequent debug messages (max once per 100ms per message type).
            if (!debug.lastLog) debug.lastLog = new Map();
            const lastLogTime = debug.lastLog.get(messageKey) || 0;

            if (now - lastLogTime > 100) {
                console.log('[UCL Privacy Debug]', message, ...args);
                debug.lastLog.set(messageKey, now);
            }
        }
    }

    /**
     * Determines if URL matches user-defined allowlist patterns with caching.
     * Allowlist overrides all classification rules to ensure essential
     * services marked by site operators always function correctly.
     * Enhanced with O(1) cache lookup to eliminate redundant pattern matching.
     * @param {string} url - The URL to check.
     * @returns {boolean} True if URL matches allowlist pattern.
     */
    function isAllowlisted(url) {
        // O(1) cache lookup prevents redundant pattern matching.
        if (allowlistCache.has(url)) {
            const cachedResult = allowlistCache.get(url);
            if (config.options.debug) {
                debug(`Allowlist cache hit for "${url}": ${cachedResult}`);
            }
            return cachedResult;
        }

        if (config.options.debug) {
            debug(`Checking allowlist for URL: ${url}`);
        }

        const result = allowlist.some(pattern => {
            if (typeof pattern === 'string') {
                // Normalize both URL and pattern for comparison.
                const normalizedUrl = url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                const normalizedPattern = pattern.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                const matches = normalizedUrl.includes(normalizedPattern);

                if (config.options.debug) {
                    debug(`Pattern "${pattern}" vs URL "${url}" - Match: ${matches}`);
                }

                return matches;
            }
            if (pattern instanceof RegExp) {
                const matches = pattern.test(url);
                if (config.options.debug) {
                    debug(`Regex pattern ${pattern} vs URL "${url}" - Match: ${matches}`);
                }
                return matches;
            }
            return false;
        });

        // Cache result for future O(1) lookups.
        allowlistCache.set(url, result);

        if (config.options.debug) {
            debug(`Allowlist check result for "${url}": ${result} (cached)`);
        }

        return result;
    }

    /**
     * Classifies a URL into a purpose category using multi-layered analysis.
     * This is a critical function that determines blocking behaviour through a
     * performance-optimised classification pipeline with comprehensive fallback strategies.
     *
     * Classification strategy (in order):
     * 1. O(1) cache lookup for previously classified URLs
     * 2. User-defined allowlist check (overrides all other rules)
     * 3. First-party domain bypass for same-origin requests
     * 4. Hash map domain lookup (exact, www-variant, subdomain matching)
     * 5. Domain suffix pattern matching with false positive prevention
     * 6. URL regex pattern analysis with context validation
     * 7. Default classification based on blocking mode configuration
     *
     * Enhanced with false positive prevention for development environments and
     * confidence scoring to improve accuracy whilst maintaining security.
     *
     * @param {string} url - The URL of the script, request, or resource to classify.
     * @returns {string} The classified purpose category: 'necessary', 'analytics', 'marketing', 'embeds', or 'unknown'.
     * @throws {Error} Does not throw - handles malformed URLs gracefully.
     * @example
     * classifyUrl('https://www.google-analytics.com/analytics.js') // Returns 'analytics'
     * classifyUrl('https://example.com/script.js') // Returns 'necessary' (first-party)
     */
    function classifyUrl(url) {
        if (!url) return 'unknown';
        if (config.options.debug) performanceMonitor.start('classification');

        // 1. Check cache for performance.
        if (urlPatternCache.has(url)) {
            if (config.options.debug) performanceMonitor.end('classification');
            return urlPatternCache.get(url);
        }

        // 2. Check user-defined allowlist.
        if (isAllowlisted(url)) {
            urlPatternCache.set(url, 'necessary');
            if (config.options.debug) performanceMonitor.end('classification');
            return 'necessary';
        }

        let hostname;
        try {
            const urlObj = new URL(url, window.location.href);
            hostname = urlObj.hostname.toLowerCase();
            // First-party requests are always considered necessary.
            if (hostname === window.location.hostname) {
                urlPatternCache.set(url, 'necessary');
                if (config.options.debug) performanceMonitor.end('classification');
                return 'necessary';
            }
        } catch (e) { hostname = null; }  // Invalid URL handling.

        // 3. Check domain lookup map (exact, no-www, and subdomain matches).
        if (hostname) {
            if (domainLookupMap.has(hostname)) {
                const result = domainLookupMap.get(hostname);
                urlPatternCache.set(url, result);
                debug(`Matched ${url} to ${result} via hash map (exact)`);
                if (config.options.debug) performanceMonitor.end('classification');
                return result;
            }
            const withoutWww = hostname.replace(/^www\./, '');
            if (withoutWww !== hostname && domainLookupMap.has(withoutWww)) {
                const result = domainLookupMap.get(withoutWww);
                urlPatternCache.set(url, result);
                debug(`Matched ${url} to ${result} via hash map (no-www)`);
                if (config.options.debug) performanceMonitor.end('classification');
                return result;
            }
            for (const [domain, purpose] of domainLookupMap) {
                if (hostname.includes(domain) || hostname.endsWith('.' + domain)) {
                    urlPatternCache.set(url, purpose);
                    debug(`Matched ${url} to ${purpose} via hash map (subdomain)`);
                    if (config.options.debug) performanceMonitor.end('classification');
                    return purpose;
                }
            }
        }

        // CLASSIFICATION FIX: Enhanced suffix pattern matching with false positive prevention
        for (const [purpose, suffixes] of Object.entries(config.domainSuffixes)) {
            for (const suffix of suffixes) {
                if (url.includes(suffix)) {
                    // Additional validation for common false positive suffixes.
                    let isValidMatch = true;

                    // For patterns like 'analytics', ensure they're not in legitimate contexts.
                    if (suffix === 'analytics' || suffix === 'stats') {
                        // Check if it's part of a legitimate first-party analytics setup.
                        if (hostname && (
                            hostname.includes('localhost') ||
                            hostname.includes('127.0.0.1') ||
                            hostname.includes('dev.') ||
                            hostname.includes('staging.')
                        )) {
                            isValidMatch = false;
                        }
                    }

                    // For patterns like 'track', be more specific about context.
                    if (suffix === 'track' || suffix === 'event') {
                        // Only match if it appears to be a tracking endpoint.
                        if (!(/\/(track|event|collect|beacon)/.test(url))) {
                            isValidMatch = false;
                        }
                    }

                    if (isValidMatch) {
                        urlPatternCache.set(url, purpose);
                        debug(`Matched ${url} to ${purpose} via suffix pattern: ${suffix}`);
                        if (config.options.debug) performanceMonitor.end('classification');
                        return purpose;
                    }
                }
            }
        }

        // CLASSIFICATION FIX: Enhanced regex pattern matching with context validation.
        for (const [purpose, patterns] of Object.entries(config.patterns)) {
            for (const pattern of patterns) {
                if (pattern.test(url)) {
                    let isValidMatch = true;
                    let confidence = 'medium';

                    // Additional validation for common false positive patterns.
                    if (purpose === 'analytics') {
                        // Check for legitimate analytics configurations.
                        if (hostname === window.location.hostname) {
                            // First-party analytics might be legitimate.
                            isValidMatch = false;
                        } else if (url.includes('config') || url.includes('settings')) {
                            // Configuration files are often legitimate.
                            confidence = 'low';
                        }
                    }

                    if (purpose === 'marketing') {
                        // Be more conservative with marketing classification.
                        if (url.includes('api') && !url.includes('ads') && !url.includes('pixel')) {
                            // API calls might be legitimate functionality.
                            isValidMatch = false;
                        }
                    }

                    // For development/staging environments, be more lenient.
                    if (hostname && (
                        hostname.includes('localhost') ||
                        hostname.includes('127.0.0.1') ||
                        hostname.includes('dev.') ||
                        hostname.includes('staging.') ||
                        hostname.includes('test.')
                    )) {
                        isValidMatch = false;
                    }

                    if (isValidMatch) {
                        urlPatternCache.set(url, purpose);
                        debug(`Matched ${url} to ${purpose} via regex pattern (confidence: ${confidence})`);
                        if (config.options.debug) performanceMonitor.end('classification');
                        return purpose;
                    }
                }
            }
        }

        // 6. Default classification based on blocking mode.
        const defaultPurpose = config.options.blockingMode === 'strict' ? 'marketing' : 'unknown';
        urlPatternCache.set(url, defaultPurpose);
        if (config.options.debug) performanceMonitor.end('classification');
        return defaultPurpose;
    }

    /**
     * Enhanced script content classification with false positive prevention.
     * This is used for <script> tags that do not have a `src` attribute. Enhanced with
     * improved pattern matching, context analysis, and confidence scoring to reduce
     * false positives while maintaining security effectiveness.
     *
     * @param {string} content - The text content of the script tag.
     * @returns {{purpose: string, service: string, confidence: string}} Classification result.
     */
    function classifyScriptContent(content) {
        if (!content || typeof content !== 'string') {
            return { purpose: 'unknown', service: 'unknown', confidence: 'none' };
        }

        // Normalise content for analysis (remove comments and extra whitespace)
        const normalizedContent = content
            .replace(/\/\*[\s\S]*?\*\//g, '') // Remove block comments
            .replace(/\/\/.*$/gm, '')          // Remove line comments
            .replace(/\s+/g, ' ')             // Normalize whitespace
            .trim();

        if (!normalizedContent) {
            return { purpose: 'necessary', service: 'empty-script', confidence: 'high' };
        }

        // Enhanced allowlist for common legitimate patterns.
        const legitimatePatterns = [
            // Common framework initialization
            /^(window\.|document\.)/,
            // jQuery/DOM ready patterns
            /\$\(document\)\.ready/,
            /\$\(function\(\)/,
            /document\.addEventListener\(['"]DOMContentLoaded/,
            // Configuration objects
            /^(var|let|const)\s+\w+\s*=\s*\{/,
            /^window\.\w+\s*=\s*\{/,
            // Function definitions
            /^function\s+\w+\s*\(/,
            // IIFE patterns (immediately invoked function expressions)
            /^\(function\s*\(/,
            // Module patterns
            /^(window\.)?\w+\s*=\s*\(function\s*\(/,
            // Common utility functions
            /console\.(log|debug|info|warn|error)/,
            // Performance/debugging utilities
            /performance\.(now|mark|measure)/,
            // Polyfills and feature detection
            /if\s*\(!\s*window\.\w+\)/,
            // CSS/style manipulation
            /\.style\.|\.className|\.classList/,
            // Event handlers
            /\.addEventListener|\.attachEvent/
        ];

        // Check if script matches legitimate patterns (reduce false positives).
        const isLikelyLegitimate = legitimatePatterns.some(pattern => pattern.test(normalizedContent));

        // Enhanced high-confidence pattern matching with context.
        for (const [purpose, patterns] of Object.entries(config.scriptPatterns)) {
            for (const item of patterns) {
                const pattern = item.pattern;

                if (normalizedContent.includes(pattern)) {
                    // Additional context validation for high-confidence matches.
                    let confidence = 'high';
                    let contextValidated = true;

                    // For analytics patterns, check for multiple indicators.
                    if (purpose === 'analytics') {
                        const analyticsIndicators = [
                            'track', 'event', 'pageview', 'analytics', 'gtag', '_gaq',
                            'mixpanel', 'amplitude', 'segment', 'dataLayer'
                        ];
                        const indicatorCount = analyticsIndicators.filter(indicator =>
                            normalizedContent.toLowerCase().includes(indicator)
                        ).length;

                        if (indicatorCount < 2 && isLikelyLegitimate) {
                            confidence = 'medium';
                            contextValidated = false;
                        }
                    }

                    // For marketing patterns, check for advertising/tracking context.
                    if (purpose === 'marketing') {
                        const marketingIndicators = [
                            'pixel', 'conversion', 'retargeting', 'remarketing', 'ad',
                            'campaign', 'affiliate', 'doubleclick', 'facebook', 'fbevents'
                        ];
                        const indicatorCount = marketingIndicators.filter(indicator =>
                            normalizedContent.toLowerCase().includes(indicator)
                        ).length;

                        if (indicatorCount < 1 && isLikelyLegitimate) {
                            confidence = 'medium';
                            contextValidated = false;
                        }
                    }

                    // Only return high-confidence classification if context is validated.
                    if (contextValidated) {
                        return {
                            purpose: purpose,
                            service: item.service || 'detected-service',
                            confidence: confidence
                        };
                    }
                }
            }
        }

        // Enhanced medium-confidence pattern matching with size and complexity checks.
        for (const [purpose, patterns] of Object.entries(config.patterns)) {
            if (patterns.some(pattern => pattern.test(normalizedContent))) {
                // Additional validation for medium-confidence matches.
                let confidence = 'medium';

                // If script is very short and matches legitimate patterns, downgrade confidence.
                if (normalizedContent.length < 200 && isLikelyLegitimate) {
                    confidence = 'low';
                }

                // If script contains configuration-like patterns, be more conservative.
                if (/^(var|let|const|window\.)\s*\w+\s*=/.test(normalizedContent)) {
                    confidence = 'low';
                }

                return {
                    purpose: purpose,
                    service: 'detected-by-pattern',
                    confidence: confidence
                };
            }
        }

        // Check for obvious tracking/analytics content even without specific patterns.
        const suspiciousKeywords = [
            'google-analytics', 'googletagmanager', 'fbevents', 'linkedin',
            'twitter', 'pinterest', 'snapchat', 'tiktok', 'amplitude',
            'mixpanel', 'hotjar', 'mouseflow', 'fullstory', 'logrocket'
        ];

        const hasSuspiciousKeywords = suspiciousKeywords.some(keyword =>
            normalizedContent.toLowerCase().includes(keyword)
        );

        if (hasSuspiciousKeywords) {
            return {
                purpose: 'analytics',
                service: 'detected-by-keywords',
                confidence: 'medium'
            };
        }

        // Check for configuration objects that might be data layer initialisation.
        if (/dataLayer\s*=|window\.dataLayer/.test(normalizedContent)) {
            return {
                purpose: 'analytics',
                service: 'datalayer-config',
                confidence: 'medium'
            };
        }
        if (config.options.blockFirstPartyInlineScripts) {
            // If content looks legitimate (common patterns), classify as necessary.
            if (isLikelyLegitimate || normalizedContent.length < 100) {
                return {
                    purpose: 'necessary',
                    service: 'likely-legitimate',
                    confidence: 'medium'
                };
            }

            // Otherwise, apply default unknown action.
            return {
                purpose: config.options.defaultUnknownAction === 'block' ? 'marketing' : 'unknown',
                service: 'unknown-script',
                confidence: 'low'
            };
        } else {
            return {
                purpose: 'necessary',
                service: 'inline-script',
                confidence: 'low'
            };
        }
    }

    /**
     * Classifies a cookie by its name using regex pattern matching.
     * Applies compiled regex patterns from configuration to determine the purpose
     * category of cookies before they are set. Used by cookie interception logic
     * to enforce consent choices at the browser API level.
     *
     * Pattern matching is case-insensitive and based on documented naming conventions
     * from major analytics and advertising platforms. Unknown cookies are classified
     * according to defaultUnknownAction configuration setting.
     *
     * @param {string} cookieName - The name of the cookie to classify (before '=' in cookie string).
     * @returns {string} The classified purpose category: 'necessary', 'analytics', 'marketing', 'embeds', or 'unknown'.
     * @example
     * classifyCookie('_ga') // Returns 'analytics' (Google Analytics)
     * classifyCookie('_fbp') // Returns 'marketing' (Facebook Pixel)
     * classifyCookie('session_id') // Returns 'marketing' or 'unknown' (depending on configuration)
     */
    function classifyCookie(cookieName, domain = '') {
        if (!cookieName || typeof cookieName !== 'string') {
            return 'marketing'; // Conservative default for invalid input.
        }

        const name = cookieName.toLowerCase();

        // 1. Check built-in cookie patterns for fast classification.
        for (const [purpose, patterns] of Object.entries(config.cookiePatterns)) {
            if (patterns.some(pattern => pattern.test(name))) {
                debug(`Cookie '${cookieName}' classified as ${purpose} via pattern match`);
                return purpose;
            }
        }

        // 2. Domain-based classification for context-aware decisions.
        if (domain) {
            try {
                const domainUrl = `https://${domain}`;
                const domainPurpose = classifyUrl(domainUrl);
                if (domainPurpose && domainPurpose !== 'unknown') {
                    debug(`Cookie '${cookieName}' classified as ${domainPurpose} via domain context: ${domain}`);
                    return domainPurpose;
                }
            } catch (error) {
                debug(`Failed to classify cookie via domain ${domain}:`, error);
            }
        }

        // 3. Conservative classification for unknown cookies.
        return config.options.defaultUnknownAction === 'block' ? 'marketing' : 'unknown';
    }

    /**
     * Checks if all non-essential consent categories have been granted.
     * This is a critical performance optimisation that enables bypassing expensive
     * classification operations when comprehensive consent is already established.
     * Used throughout the blocking pipeline to skip URL analysis and DOM processing.
     *
     * Only returns true when user has explicitly consented AND all three optional
     * categories (analytics, marketing, embeds) are enabled. 'necessary' is always
     * granted by default and does not affect this calculation.
     *
     * @returns {boolean} True if user has consented and all optional categories are granted.
     * @example
     * // User has granted all permissions
     * hasAllConsentGranted() // Returns true - classification can be bypassed
     * // User has only granted analytics
     * hasAllConsentGranted() // Returns false - classification still required
     */
    function hasAllConsentGranted() {
        return hasConsented && consentState.analytics && consentState.marketing && consentState.embeds;
    }

    /**
     * Evaluates whether a resource should be blocked based on current consent state.
     * Considers user consent preferences and applies defaultUnknownAction policy
     * for resources that cannot be classified. Core blocking decision engine used
     * throughout the consent management system.
     *
     * Blocking logic:
     * - 'necessary': Never blocked (essential for site functionality)
     * - Known categories: Blocked unless user has granted specific consent
     * - 'unknown': Blocked if defaultUnknownAction is 'block', allowed if 'allow'
     *
     * @param {string} purpose - The purpose category to evaluate: 'necessary', 'analytics', 'marketing', 'embeds', or 'unknown'.
     * @returns {boolean} True if the resource should be blocked, false if it should be allowed.
     * @example
     * shouldBlock('necessary') // Always returns false
     * shouldBlock('analytics') // Returns !consentState.analytics
     * shouldBlock('unknown') // Depends on defaultUnknownAction configuration
     */
    function shouldBlock(purpose) {
        if (purpose === 'unknown' && config.options.defaultUnknownAction === 'allow') return false;
        return !consentState[purpose];
    }

    /**
     * Smart URL classification with performance optimisation for full consent scenarios.
     * Implements intelligent bypass logic that skips expensive classification operations
     * when all consent categories have been granted, providing significant performance
     * improvements in post-consent page interactions and dynamic content loading.
     *
     * Returns null as a special signal when no classification is needed (all consent
     * granted), allowing calling code to bypass blocking checks entirely. Otherwise
     * delegates to standard classifyUrl function for complete analysis.
     *
     * @param {string} url - The URL to classify or bypass.
     * @returns {string|null} Purpose category string if classification needed, null if all consent granted and classification can be bypassed.
     * @example
     * // All consent granted scenario
     * classifyUrlSmart('https://analytics.example.com/track') // Returns null (bypass)
     * // Partial consent scenario
     * classifyUrlSmart('https://analytics.example.com/track') // Returns 'analytics'
     */
    function classifyUrlSmart(url) {
        // Performance bypass: Skip all classification if all consent granted.
        if (hasAllConsentGranted()) {
            debug(`All consent granted, bypassing classification for: ${url}`);
            return null; // Signal that no blocking is needed.
        }

        // Standard classification for selective consent scenarios.
        return classifyUrl(url);
    }

    /*
     * WeakSet for tracking processed elements prevents duplicate blocking operations.
     * Memory-efficient approach that automatically cleans up when DOM elements
     * are removed, preventing memory leaks in dynamic applications.
     *
     * Added consent state tracking for smart re-classification.
     */
    const blockedElements = new WeakSet();
    let processedElements = new WeakSet(); // Track all processed elements to avoid reprocessing

    /**
     * Blocks a script element by modifying its attributes to prevent execution.
     * Implements the core script blocking mechanism by changing the type attribute
     * to 'text/plain', which prevents browser JavaScript engine execution whilst
     * preserving the element in the DOM for potential restoration.
     *
     * Blocking process:
     * 1. Change type to 'text/plain' (prevents execution)
     * 2. Remove src attribute (prevents network request)
     * 3. Store original attributes in data-* attributes (enables restoration)
     * 4. Add CSS classes for styling and identification
     * 5. Update blocking statistics
     *
     * Uses WeakSet tracking to prevent duplicate blocking operations on the same
     * element during DOM mutations or re-processing events.
     *
     * @param {HTMLScriptElement} script - The script element to block.
     * @param {object} classification - Classification result from analysis.
     * @param {string} classification.purpose - Purpose category ('analytics', 'marketing', etc.).
     * @param {string} classification.service - Service name or hostname for identification.
     * @param {string} source - Detection context: 'initial', 'mutation', 'createElement', etc.
     * @returns {void} Modifies the script element in-place.
     */
    function blockScript(script, classification, source) {
        if (blockedElements.has(script)) return;
        blockedElements.add(script);

        const { purpose, service } = classification;
        script.setAttribute('data-blocked-purpose', purpose);
        script.setAttribute('data-blocked-service', service);
        script.setAttribute('data-blocked-source', source);
        script.setAttribute('data-original-type', script.type || 'text/javascript');

        if (script.src) {
            script.setAttribute('data-original-src', script.src);
            script.removeAttribute('src');
        }
        script.type = 'text/plain';  // This is the key step that prevents execution.
        script.classList.add('privacy-blocked', `privacy-blocked-${purpose}`);

        stats.blocked.scripts++;
        if (stats.byPurpose[purpose]) stats.byPurpose[purpose].scripts++;
        log(`Blocked ${purpose} script: ${service} from ${source}`);
    }

    /**
     * Determines if a script element is essential for consent manager functionality.
     * Configuration and necessary scripts must be allowed to execute to prevent the
     * consent manager from blocking its own setup code or essential site functionality.
     * This prevents circular blocking scenarios and ensures core features remain operational.
     *
     * Detection methods:
     * 1. Explicit data-privacy-necessary="true" attribute marking
     * 2. Explicit data-privacy-config="true" attribute marking
     * 3. Content analysis for 'window.privacyConfig' configuration objects
     *
     * These scripts execute regardless of consent state to maintain system integrity
     * and enable proper consent manager initialisation and configuration.
     *
     * @param {HTMLScriptElement} script - The script element to evaluate.
     * @returns {boolean} True if the script should be exempt from consent blocking.
     * @example
     * // Script with configuration
     * isConfigurationScript(scriptWithPrivacyConfig) // Returns true
     * // Regular tracking script
     * isConfigurationScript(analyticsScript) // Returns false
     */
    function isConfigurationScript(script) {
        /*
         * Check for explicit data attributes marking the script as essential.
         * Sites can use data-privacy-necessary="true" for core functionality scripts
         * or data-privacy-config="true" specifically for consent configuration.
         */
        if (script.getAttribute('data-privacy-necessary') === 'true' ||
            script.getAttribute('data-privacy-config') === 'true') {
            return true;
        }

        /*
         * Detect inline configuration scripts by content analysis.
         * Scripts containing 'window.privacyConfig' are setting up the consent
         * manager's configuration and must execute before blocking takes effect.
         */
        const content = script.textContent || script.innerHTML;
        if (content && content.includes('window.privacyConfig')) {
            return true;
        }

        return false;
    }

    /**
     * Evaluates and processes script elements for consent compliance.
     * Handles both external scripts via URL classification and inline scripts
     * through content analysis. Configuration scripts are exempted to prevent
     * circular blocking of the consent manager's own functionality.
     *
     * Enhanced with consent-aware processing to bypass classification when
     * consent is already granted, improving performance and reducing overhead.
     * Added smart reprocessing to avoid duplicate work on already-processed elements.
     *
     * @param {HTMLScriptElement} script - The script element to evaluate.
     * @param {string} [source='unknown'] - Detection source for debugging.
     */
    function processScript(script, source = 'unknown') {
        /*
         * Skip already processed elements unless consent state changed.
         * Prevents redundant processing of the same scripts during DOM mutations.
         */
        if (processedElements.has(script)) {
            const currentConsentState = JSON.stringify(consentState);
            if (lastConsentState === currentConsentState) {
                if (config.options.debug) {
                    debug(`Script already processed, skipping: ${script.src || 'inline'}`);
                }
                return;
            }
            // Consent state changed, allow reprocessing.
        }

        // Mark as processed to prevent future reprocessing.
        processedElements.add(script);

        /*
         * Early exit for configuration scripts to prevent circular blocking.
         * These scripts are essential for consent manager functionality and must
         * be allowed to execute regardless of consent state.
         */
        if (isConfigurationScript(script)) {
            debug(`Skipped configuration script from ${source}:`,
                script.getAttribute('data-privacy-necessary') ? 'marked as necessary' :
                    script.getAttribute('data-privacy-config') ? 'marked as config' :
                        'contains privacyConfig');
            return;
        }

        const src = script.src;
        const content = script.textContent || script.innerHTML;

        // Performance optimisation: Global consent bypass check first.
        if (hasAllConsentGranted()) {
            if (config.options.debug) {
                debug(`Script bypass: ${src || 'inline'} (all consent granted)`);
            }
            return;
        }

        // Performance optimisation: Quick allowlist check before classification.
        if (src && isAllowlisted(src)) {
            if (config.options.debug) {
                debug(`Script allowlisted: ${src}`);
            }
            return;
        }

        let classification;

        /*
         * Classify scripts based on external URL or inline content.
         * External scripts are classified by URL patterns and domain lists.
         * Inline scripts are classified by content analysis of function calls.
         */
        if (src) {
            const purpose = classifyUrl(src);
            classification = { purpose: purpose, service: new URL(src, window.location.href).hostname, type: 'external' };
        } else if (content) {
            classification = classifyScriptContent(content);
            classification.type = 'inline';
        } else {
            return;  // Skip empty script elements.
        }

        /*
         * Consent-aware processing optimisation:
         * Skip blocking logic entirely if user has already granted consent
         * for this purpose category. This prevents unnecessary DOM manipulation
         * and improves performance when consent is already established.
         */
        if (hasConsented && consentState[classification.purpose]) {
            if (config.options.debug) {
                debug(`Script allowed: ${src || 'inline'} (${classification.purpose} consent granted)`);
            }
            return;
        }

        if (shouldBlock(classification.purpose)) {
            blockScript(script, classification, source);
        }
    }

    /**
     * Cache for proxy pattern detection to improve performance.
     * Maps pathname strings to boolean classification results to avoid repeated
     * pattern matching operations. Critical for maintaining responsiveness when
     * processing multiple proxy URLs in rapid succession.
     */
    const proxyPatternCache = new Map();

    /**
     * Detects if a URL pathname matches common oEmbed or CMS proxy patterns.
     * Identifies proxy endpoints that serve third-party content through first-party
     * domains, which could circumvent consent controls. Critical security measure
     * for maintaining GDPR compliance when sites use CMS proxy functionality.
     *
     * Uses cached pattern matching for performance with externally-maintained
     * pattern lists from JSON configuration. Patterns identify common CMS proxy
     * endpoints like '/oembed/', '/embed/', '/proxy/', and platform-specific variations.
     *
     * When a proxy is detected, the system extracts the actual destination URL
     * from request parameters and applies consent classification to the real
     * third-party service rather than the first-party proxy domain.
     *
     * @param {string} pathname - URL pathname component to analyse (e.g., '/oembed/proxy').
     * @returns {boolean} True if pathname matches known proxy endpoint patterns.
     * @example
     * isProxyPattern('/oembed/') // Returns true
     * isProxyPattern('/embed/youtube') // Returns true
     * isProxyPattern('/regular-page') // Returns false
     */
    function isProxyPattern(pathname) {
        if (proxyPatternCache.has(pathname)) {
            return proxyPatternCache.get(pathname);
        }

        // Apply externally-maintained proxy patterns from JSON configuration.
        const patterns = proxyPatterns;

        const result = patterns.some(pattern => pathname.includes(pattern));
        proxyPatternCache.set(pathname, result);

        if (config.options.debug) {
            debug(`Proxy pattern check for "${pathname}": ${result}`);
        }

        return result;
    }

    /**
     * Enhanced proxy URL extraction with comprehensive security validation.
     * Handles multiple encoding strategies (URL encoding, Base64) and parameter names
     * used by different CMS platforms and oEmbed implementations. Includes comprehensive
     * security checks to prevent abuse through malformed URLs, redirect loops, domain
     * confusion attacks, and DoS attacks.
     *
     * This function is critical for maintaining consent integrity when sites use proxy
     * services to serve third-party content through their own domains.
     *
     * @param {URL} srcUrl - Proxy URL requiring parameter extraction and validation.
     * @returns {string|null} Validated target URL or null if extraction fails.
     */
    function extractProxiedUrl(srcUrl) {
        const paramCandidates = ['url', 'src', 'embed', 'source', 'target'];
        const currentHostname = window.location.hostname.toLowerCase();
        const currentOrigin = window.location.origin.toLowerCase();

        for (const param of paramCandidates) {
            const value = srcUrl.searchParams.get(param);
            if (!value) continue;

            try {
                // URL length validation prevents DoS attacks via oversized parameters.
                if (value.length > 2048) {
                    if (config.options.debug) {
                        const truncated = value.slice(0, 100);
                        debug('SECURITY: Blocked oversized proxy URL parameter:', truncated, '...');
                    }
                    continue;
                }

                // Validate parameter contains only safe characters before processing.
                if (!/^[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+$/.test(value)) {
                    debug('SECURITY: Blocked proxy URL with unsafe characters:', value);
                    continue;
                }

                // Handle multiple encoding layers with enhanced validation.
                let decodedValue;
                try {
                    decodedValue = decodeURIComponent(value);

                    // Prevent double-decoding attacks.
                    if (decodedValue !== value && decodedValue.includes('%')) {
                        debug('SECURITY: Blocked potential double-encoding attack:', value);
                        continue;
                    }
                } catch (decodeError) {
                    debug('SECURITY: Invalid URL encoding in proxy parameter:', value);
                    continue;
                }

                // Enhanced validation for HTTP/HTTPS URLs.
                if (decodedValue.startsWith('http://') || decodedValue.startsWith('https://')) {
                    try {
                        const targetUrl = new URL(decodedValue);
                        const targetHostname = targetUrl.hostname.toLowerCase();
                        const targetOrigin = targetUrl.origin.toLowerCase();

                        // Comprehensive redirect loop prevention.
                        if (targetHostname === currentHostname || targetOrigin === currentOrigin) {
                            debug('SECURITY: Blocked redirect loop - same hostname/origin:', decodedValue);
                            continue;
                        }

                        // Block localhost and private IP ranges.
                        if (targetHostname === 'localhost' ||
                            targetHostname.startsWith('127.') ||
                            targetHostname.startsWith('192.168.') ||
                            targetHostname.startsWith('10.') ||
                            /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(targetHostname) ||
                            targetHostname.startsWith('[::1]') ||
                            targetHostname === '0.0.0.0') {
                            debug('SECURITY: Blocked private/local network target:', decodedValue);
                            continue;
                        }

                        // Block file:// and other dangerous schemes.
                        const scheme = targetUrl.protocol.toLowerCase();
                        if (scheme !== 'http:' && scheme !== 'https:') {
                            debug('SECURITY: Blocked non-HTTP scheme:', scheme, decodedValue);
                            continue;
                        }

                        // Validate hostname format to prevent domain confusion.
                        if (!/^[a-z0-9.-]+$/i.test(targetHostname) ||
                            targetHostname.includes('..') ||
                            targetHostname.startsWith('.') ||
                            targetHostname.endsWith('.') ||
                            targetHostname.length > 253) {
                            debug('SECURITY: Blocked malformed hostname:', targetHostname);
                            continue;
                        }

                        // Additional checks for suspicious domains.
                        if (targetHostname.includes(currentHostname) && targetHostname !== currentHostname) {
                            debug('SECURITY: Blocked suspicious domain containing current hostname:', targetHostname);
                            continue;
                        }

                        debug(`SECURITY: Validated proxy target URL: ${decodedValue}`);
                        return decodedValue;
                    } catch (urlError) {
                        debug('SECURITY: Invalid URL format in proxy parameter:', decodedValue, urlError.message);
                        continue;
                    }
                }
            } catch (e) {
                // Attempt Base64 decoding for CMS platforms that encode proxy parameters.
                try {
                    // Validate Base64 format before decoding.
                    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(value)) {
                        debug('SECURITY: Invalid Base64 format:', value);
                        continue;
                    }

                    const decoded = atob(value);

                    // Validate decoded length.
                    if (decoded.length > 2048) {
                        debug('SECURITY: Blocked oversized Base64 decoded URL:', decoded.length);
                        continue;
                    }

                    if (decoded.startsWith('http://') || decoded.startsWith('https://')) {
                        try {
                            const validatedUrl = new URL(decoded);
                            const targetHostname = validatedUrl.hostname.toLowerCase();
                            const targetOrigin = validatedUrl.origin.toLowerCase();

                            // Apply same validation rules as above.
                            if (targetHostname === currentHostname || targetOrigin === currentOrigin) {
                                debug('SECURITY: Blocked Base64 redirect loop:', decoded);
                                continue;
                            }

                            // Block private networks for Base64 decoded URLs too.
                            if (targetHostname === 'localhost' ||
                                targetHostname.startsWith('127.') ||
                                targetHostname.startsWith('192.168.') ||
                                targetHostname.startsWith('10.') ||
                                /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(targetHostname) ||
                                targetHostname.startsWith('[::1]') ||
                                targetHostname === '0.0.0.0') {
                                debug('SECURITY: Blocked Base64 private network target:', decoded);
                                continue;
                            }

                            // Validate scheme for Base64 decoded URLs.
                            const scheme = validatedUrl.protocol.toLowerCase();
                            if (scheme !== 'http:' && scheme !== 'https:') {
                                debug('SECURITY: Blocked Base64 non-HTTP scheme:', scheme, decoded);
                                continue;
                            }

                            debug(`SECURITY: Validated Base64 proxy target URL: ${decoded}`);
                            return decoded;
                        } catch (base64UrlError) {
                            debug('SECURITY: Invalid Base64 decoded URL format:', decoded, base64UrlError.message);
                            continue;
                        }
                    }
                } catch (e2) {
                    // Skip malformed Base64 values — prevents processing invalid data.
                    debug('SECURITY: Base64 decoding failed for proxy parameter:', value);
                    continue;
                }
            }
        }

        return null;
    }

    /**
     * Analyses surrounding DOM context to discover embed destination URLs.
     * When proxy parameter extraction fails, this heuristic analysis searches for
     * accessibility fallback links that many CMS systems include near embedded content.
     * Essential for maintaining consent enforcement when direct URL extraction methods fail.
     *
     * Search methodology:
     * 1. Locate nearest container element (div, section, article, aside)
     * 2. Search container for links to recognised embed platforms
     * 3. Match against known embed domain patterns (YouTube, Vimeo, social media)
     * 4. Return first matching URL for consent classification
     *
     * This approach leverages common accessibility practices where CMS systems
     * provide direct links to embedded content for users with JavaScript disabled.
     *
     * @param {HTMLIFrameElement} iframe - The blocked iframe requiring context analysis.
     * @returns {string|null} Discovered embed destination URL or null if no related links found.
     * @example
     * // Container with YouTube fallback link
     * findRelatedEmbedLink(blockedYouTubeIframe) // Returns 'https://youtube.com/watch?v=...'
     * // No related links found
     * findRelatedEmbedLink(genericIframe) // Returns null
     */
    function findRelatedEmbedLink(iframe) {
        const container = iframe.closest('div, section, article, aside');
        if (!container) return null;

        // Search for fallback links to recognised embed platforms within DOM container.
        const embedDomainPatterns = [
            'youtube.com', 'youtu.be', 'vimeo.com', 'twitter.com', 'instagram.com',
            'facebook.com', 'tiktok.com', 'soundcloud.com', 'spotify.com'
        ];

        const links = container.querySelectorAll('a[href]');
        for (const link of links) {
            const href = link.href;
            for (const domain of embedDomainPatterns) {
                if (href.includes(domain)) {
                    debug(`Found related embed link: ${href}`);
                    return href;
                }
            }
        }

        return null;
    }

    /**
     * Evaluates iframe elements for consent compliance with comprehensive proxy detection.
     * Implements sophisticated analysis to identify third-party content served through
     * first-party proxy URLs, ensuring GDPR compliance cannot be circumvented through
     * technical domain proxying whilst preserving legitimate first-party functionality.
     *
     * Processing pipeline:
     * 1. Performance bypass check for full consent scenarios
     * 2. First-party vs third-party domain analysis
     * 3. Proxy pattern detection for first-party domains
     * 4. Proxy parameter extraction and validation with security checks
     * 5. Fallback DOM context analysis for embed discovery
     * 6. Purpose classification of actual destination URL
     * 7. Consent enforcement and placeholder generation
     *
     * Security features include redirect loop prevention, private network blocking,
     * malformed URL handling, and comprehensive input validation to prevent abuse.
     *
     * @param {HTMLIFrameElement} iframe - The iframe element to evaluate for consent compliance.
     * @returns {void} Modifies iframe attributes and dispatches events as needed.
     * @example
     * // Direct third-party iframe
     * processIframe(youtubeIframe) // Blocks if embeds consent not granted
     * // First-party proxy iframe
     * processIframe(cmsProxyIframe) // Extracts real URL, applies consent to destination
     */
    function processIframe(iframe) {
        const src = iframe.src || iframe.getAttribute('data-src');
        if (!src || src === 'about:blank') return;

        // Bypass all processing if all consent granted.
        if (hasAllConsentGranted()) {
            debug(`All consent granted, bypassing iframe processing: ${src}`);
            return;
        }

        let actualTargetUrl = null;
        let purpose = 'embeds';  // Default for third-party iframes

        try {
            const srcUrl = new URL(src, window.location.href);

            // Enhanced first-party analysis with proxy circumvention detection.
            if (srcUrl.hostname === window.location.hostname) {
                // Evaluate proxy patterns when first-party proxy blocking is enabled.
                if (config.options.blockFirstPartyProxies && isProxyPattern(srcUrl.pathname)) {
                    // Extract and validate the proxied destination URL from parameters.
                    const targetUrl = extractProxiedUrl(srcUrl);
                    if (targetUrl) {
                        // Verify target URL is genuinely external to prevent false positives.
                        try {
                            const target = new URL(targetUrl);
                            if (target.hostname !== window.location.hostname) {
                                // Apply consent classification to actual destination, not proxy domain.
                                purpose = classifyUrl(targetUrl);
                                actualTargetUrl = targetUrl;
                                debug(`First-party proxy iframe detected. Target: ${targetUrl}, Purpose: ${purpose}`);
                            } else {
                                // Internal redirect detected — treat as legitimate first-party content.
                                debug(`Internal redirect detected in proxy iframe: ${targetUrl}`);
                                return;
                            }
                        } catch (e) {
                            // Malformed target URL — default to safe first-party classification.
                            debug(`Invalid target URL in proxy iframe: ${targetUrl}`);
                            return;
                        }
                    } else {
                        // Proxy parameter extraction failed — attempt DOM context analysis.
                        const relatedLink = findRelatedEmbedLink(iframe);
                        if (relatedLink) {
                            purpose = classifyUrl(relatedLink);
                            actualTargetUrl = relatedLink;
                            debug(`Found related embed link for proxy iframe: ${relatedLink}, Purpose: ${purpose}`);
                        } else {
                            // No external destination identified — treat as standard first-party iframe.
                            return;
                        }
                    }
                } else {
                    // Standard first-party iframe or proxy detection disabled in configuration.
                    return;
                }
            } else {
                // Third-party iframe — apply standard external content classification.
                actualTargetUrl = src;
            }
        } catch (e) {
            // Malformed URLs are treated as potentially dangerous and blocked by default.
            debug(`Invalid iframe URL: ${src}`, e);
            actualTargetUrl = src;
        }

        // Apply allowlist check to final target URL (proxy destination or original source).
        if (isAllowlisted(actualTargetUrl || src)) return;

        // Enforce blocking if the classified purpose requires user consent.
        if (shouldBlock(purpose)) {
            iframe.setAttribute('data-blocked-src', src);
            iframe.setAttribute('data-blocked-purpose', purpose);
            if (actualTargetUrl && actualTargetUrl !== src) {
                iframe.setAttribute('data-blocked-actual-url', actualTargetUrl);
            }
            iframe.removeAttribute('src');
            iframe.style.display = 'none';

            stats.blocked.iframes++;
            if (stats.byPurpose[purpose]) stats.byPurpose[purpose].iframes++;

            const logUrl = actualTargetUrl || src;
            log(`Blocked ${purpose} iframe: ${logUrl}${actualTargetUrl && actualTargetUrl !== src ? ` (via proxy: ${src})` : ''}`);

            // Dispatch event to UI layer for placeholder generation and user notification.
            window.dispatchEvent(new CustomEvent('ucl-privacy-iframe-blocked', { detail: { iframe } }));
        }
    }

    /*
     * Intercept native browser APIs to enforce consent choices.
     * This is the core of the blocking mechanism. By overriding these functions,
     * we can inspect and block actions before they happen.
     */

    /*
     * Intercept cookie creation via document.cookie property.
     * Overrides the native setter to classify and block non-consented cookies
     * whilst preserving necessary functionality like authentication tokens.
     */
    if (originalMethods.cookieSetter) {
        Object.defineProperty(document, 'cookie', {
            get: function() {
                return originalMethods.cookieGetter.call(document);
            },
            set: function(value) {
                // Bypass classification if all consent granted.
                if (hasAllConsentGranted()) {
                    if (config.options.debug) {
                        debug(`Cookie bypass: ${value.split('=')[0].trim()} (all consent granted)`);
                    }
                    originalMethods.cookieSetter.call(document, value);
                    return;
                }

                const cookieName = value.split('=')[0].trim();
                const purpose = classifyCookie(cookieName);
                if (shouldBlock(purpose)) {
                    stats.blocked.cookies++;
                    if (stats.byPurpose[purpose]) stats.byPurpose[purpose].cookies++;
                    debug(`Blocked ${purpose} cookie: ${cookieName}`);
                    return;  // Prevent the cookie from being set.
                }
                originalMethods.cookieSetter.call(document, value);
            }
        });
    }

    /*
     * Intercept fetch requests for network-level blocking.
     * Modern applications use fetch API extensively for tracking beacons
     * and analytics data collection. Blocking at this level prevents
     * data transmission before it occurs.
     */
    window.fetch = function(...args) {
        const url = typeof args[0] === 'string' ? args[0] : args[0].url;

        // Bypass classification if all consent granted.
        const purpose = classifyUrlSmart(url);
        if (purpose === null) {
            // All consent granted, allow request without blocking checks.
            if (config.options.debug) {
                debug(`Fetch bypass: ${url} (all consent granted)`);
            }
            return originalMethods.fetch.apply(this, args);
        }

        if (shouldBlock(purpose)) {
            stats.blocked.requests++;
            if (stats.byPurpose[purpose]) stats.byPurpose[purpose].requests++;
            log(`Blocked ${purpose} fetch request: ${url}`);
            // Return a resolved promise with an empty response to prevent errors in calling code.
            return Promise.resolve(new Response('', { status: 200, statusText: 'Blocked by Privacy Protection' }));
        }
        return originalMethods.fetch.apply(this, args);
    };

    /*
     * Intercept XMLHttpRequest for legacy tracking script compatibility.
     * Many analytics and marketing tools still use XHR for data transmission.
     * Comprehensive override maintains request lifecycle whilst blocking network access.
     *
     * Performance optimized with consent-aware bypass to skip expensive
     * classification when all consent categories are already granted.
     */
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
        // Performance optimization: Bypass classification if all consent granted
        const purpose = classifyUrlSmart(url);
        if (purpose === null) {
            // All consent granted, allow request without blocking checks.
            if (config.options.debug) {
                debug(`XHR bypass: ${url} (all consent granted)`);
            }
            return originalMethods.xmlHttpRequestOpen.apply(this, [method, url, ...rest]);
        }

        if (shouldBlock(purpose)) {
            stats.blocked.requests++;
            if (stats.byPurpose[purpose]) stats.byPurpose[purpose].requests++;
            log(`Blocked ${purpose} XHR request: ${url}`);

            /*
             * Use Blob URL approach with proper resource management.
             * This simulates a complete request lifecycle with proper events whilst maintaining
             * XMLHttpRequest state machine integrity. Blob URLs prevent network requests whilst
             * allowing proper event dispatch and state transitions.
             * Enhanced with comprehensive cleanup to prevent memory leaks.
             */
            const blockedResponse = new Blob([''], { type: 'text/plain' });
            const blobUrl = URL.createObjectURL(blockedResponse);

            // Track blob URL for cleanup even if send() is never called.
            const blobCleanupTimer = setTimeout(() => {
                try {
                    URL.revokeObjectURL(blobUrl);
                } catch (e) {
                    // Ignore cleanup errors - blob may already be revoked.
                }
            }, 10000); // Fallback cleanup after 10 seconds.

            // Call original open with blob URL to maintain proper state machine.
            originalMethods.xmlHttpRequestOpen.apply(this, [method, blobUrl, ...rest]);

            /*
             * Override send to simulate complete request lifecycle with proper events.
             * This ensures blocked requests behave like successful empty responses rather
             * than failed requests, preventing error handling in tracking code.
             * Enhanced with improved memory management and error handling.
             */
            const originalSend = this.send;
            this.send = (_body) => {
                // Clear the fallback cleanup timer since we're handling cleanup explicitly.
                clearTimeout(blobCleanupTimer);

                try {
                    // Dispatch loadstart event for compatibility.
                    this.dispatchEvent(new ProgressEvent('loadstart', { lengthComputable: true, loaded: 0, total: 0 }));

                    // Call original send to let browser handle state transitions.
                    originalSend.call(this);

                    // Clean up blob URL after use to prevent memory leaks.
                    // Use requestAnimationFrame for better timing with browser lifecycle.
                    requestAnimationFrame(() => {
                        try {
                            URL.revokeObjectURL(blobUrl);
                        } catch (e) {
                            // Blob URL may already be revoked, ignore error.
                            if (config.options.debug) {
                                console.log('UCL Privacy: Blob URL already revoked or invalid:', blobUrl);
                            }
                        }
                    });
                } catch (error) {
                    // Ensure blob URL is cleaned up even if send() fails.
                    clearTimeout(blobCleanupTimer);
                    try {
                        URL.revokeObjectURL(blobUrl);
                    } catch (e) {
                        // Ignore cleanup errors.
                    }
                    throw error; // Re-throw the original error.
                }
            };

            /*
             * Override response methods to return empty but valid responses.
             * Prevents tracking scripts from detecting blocking and ensures graceful degradation.
             */
            this.getAllResponseHeaders = () => '';
            this.getResponseHeader = () => null;
            this.setRequestHeader = function(_header, _value) {
                // Accept parameters but perform no operation for blocked requests.
                // This maintains compatibility while preventing actual header setting.
                return;
            };
            return;
        }
        return originalMethods.xmlHttpRequestOpen.apply(this, [method, url, ...rest]);
    };

    /*
     * Intercept dynamic element creation via document.createElement.
     * Catches dynamically inserted scripts and iframes that bypass DOM mutation
     * observation. Property-level interception ensures blocking occurs before
     * network requests are initiated.
     */
    document.createElement = function(tagName) {
        const element = originalMethods.createElement.call(document, tagName);
        const lowerTagName = tagName.toLowerCase();

        /*
         * Special handling for script elements to intercept src assignment.
         * Dynamically created scripts must be intercepted at the property level
         * as they bypass normal DOM parsing and mutation observation.
         */
        if (lowerTagName === 'script') {
            let scriptSrc = '';
            Object.defineProperty(element, 'src', {
                get: () => scriptSrc,
                set: function(value) {
                    scriptSrc = value;

                    // Performance optimization: Bypass if all consent granted.
                    if (hasAllConsentGranted()) {
                        debug(`All consent granted, allowing createElement script: ${value}`);
                        element.setAttribute('src', value);
                        return;
                    }

                    const purpose = classifyUrl(value);
                    if (shouldBlock(purpose)) {
                        // Block immediately upon src assignment to prevent loading.
                        debug(`Blocking createElement script: ${value}`, { element: element.tagName });
                        blockScript(element, { purpose: purpose, service: new URL(value, window.location.href).hostname }, 'createElement');
                        return;
                    }
                    element.setAttribute('src', value);
                }
            });
        } else if (lowerTagName === 'iframe') {
            let iframeSrc = '';
            Object.defineProperty(element, 'src', {
                get: () => iframeSrc,
                set: function(value) {
                    iframeSrc = value;

                    // Performance optimization: Bypass if all consent granted.
                    if (hasAllConsentGranted()) {
                        debug(`All consent granted, allowing createElement iframe: ${value}`);
                        element.setAttribute('src', value);
                        return;
                    }

                    const purpose = classifyUrl(value);
                    if (shouldBlock(purpose)) {
                        element.setAttribute('data-blocked-src', value);
                        element.setAttribute('data-blocked-purpose', purpose);
                        // Do not set the src attribute to prevent loading.
                        return;
                    }
                    element.setAttribute('src', value);
                }
            });
        }
        return element;
    };

    /**
     * Processes pre-existing DOM elements for consent evaluation.
     * Essential first-pass processing that evaluates all scripts and iframes present
     * in the DOM before blocker initialisation completed. Ensures comprehensive
     * protection from the moment the script loads, catching any elements that
     * were parsed before consent enforcement began.
     *
     * Excludes document.currentScript to prevent the consent manager from blocking
     * itself during initialisation. Uses 'initial' source tag for statistics and
     * debugging to distinguish from dynamically-added content.
     *
     * @returns {void} Processes all existing script and iframe elements in-place.
     */
    function processExistingElements() {
        document.querySelectorAll('script').forEach(script => {
            // Avoid processing the current script itself.
            if (script !== document.currentScript) processScript(script, 'initial');
        });
        document.querySelectorAll('iframe').forEach(processIframe);
    }

    /*
     * MutationObserver monitors dynamic DOM changes for comprehensive protection.
     * Essential for single-page applications and lazy-loaded content where scripts
     * and iframes are inserted after initial page load.
     */
    const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
            mutation.addedNodes.forEach(node => {
                if (node.nodeType !== 1) return;  // Only process element nodes.

                if (node.tagName === 'SCRIPT') {
                    processScript(node, 'mutation');
                } else if (node.tagName === 'IFRAME') {
                    processIframe(node);
                } else if (node.querySelectorAll) {
                    // Recursively check for nested scripts and iframes within added elements.
                    node.querySelectorAll('script').forEach(script => processScript(script, 'mutation-child'));
                    node.querySelectorAll('iframe').forEach(processIframe);
                }
            });
        });
    });

    /**
     * Initiates comprehensive DOM monitoring and cross-script communication.
     * Establishes MutationObserver for dynamic content in single-page applications
     * and frameworks with client-side routing. Processes existing elements and
     * dispatches configuration data to UI components via custom events.
     *
     * Sets up monitoring for:
     * - Dynamically inserted script and iframe elements
     * - Content loaded via AJAX or fetch operations
     * - Single-page application route changes
     * - Lazy-loaded embed content
     *
     * Dispatches 'ucl-privacy-blocker-ready' event with validated platform
     * mapping configuration for UI layer integration.
     *
     * @returns {void} Initialises monitoring and dispatches configuration events.
     */
    function startObserving() {
        processExistingElements();
        observer.observe(document.documentElement, { childList: true, subtree: true });
        /*
         * Cross-script communication event with configuration data.
         * Passes validated platform mapping extensions to UI script for enhanced
         * user experience in consent placeholders. Event-driven architecture
         * maintains loose coupling between blocker and UI components.
         */
        window.dispatchEvent(new CustomEvent('ucl-privacy-blocker-ready', {
            detail: {
                platformMapping: userConfig.platformMapping || {}
            }
        }));
    }

    /*
     * Initialise blocker immediately to catch inline scripts during parsing.
     * Processing existing elements synchronously ensures no scripts execute
     * before consent evaluation, maintaining GDPR compliance from page load.
     */
    processExistingElements();

    // Set up DOM observation for future changes.
    if (document.readyState === 'loading') {
        // Start observing immediately, even during parsing.
        observer.observe(document.documentElement, { childList: true, subtree: true });
        /*
         * Cross-script communication event with configuration data.
         * Passes validated platform mapping extensions to UI script for enhanced
         * user experience in consent placeholders. Event-driven architecture
         * maintains loose coupling between blocker and UI components.
         */
        window.dispatchEvent(new CustomEvent('ucl-privacy-blocker-ready', {
            detail: {
                platformMapping: userConfig.platformMapping || {}
            }
        }));
        document.addEventListener('DOMContentLoaded', () => {
            // Process any elements that might have been missed during initial parsing.
            processExistingElements();
        });
    } else {
        startObserving();
    }

    /**
     * Retrieves all cookies from the current domain with error handling.
     * Uses the preserved original cookie getter to access cookies even
     * when the cookie setter has been intercepted. Provides safe parsing
     * with comprehensive error recovery for malformed cookie strings.
     *
     * @returns {Array<Object>} Array of cookie objects with name, value, and metadata.
     * @returns {string} returns[].name - Cookie name.
     * @returns {string} returns[].value - Cookie value.
     * @returns {string} returns[].domain - Inferred domain from current location.
     * @returns {string} returns[].purpose - Classified consent category.
     */
    function getCurrentCookies() {
        try {
            const cookieString = originalMethods.cookieGetter?.call(document) || '';
            if (!cookieString.trim()) {
                return [];
            }

            const currentDomain = window.location.hostname;
            const cookies = [];

            // Parse cookie string with robust error handling.
            cookieString.split(';').forEach(cookie => {
                const trimmed = cookie.trim();
                if (!trimmed) return;

                const [nameValue] = trimmed.split('=', 2);
                if (!nameValue) return;

                const name = nameValue.trim();
                if (!name) return;

                // Extract value safely with URL decoding attempt.
                let value = '';
                const equalIndex = trimmed.indexOf('=');
                if (equalIndex !== -1) {
                    value = trimmed.substring(equalIndex + 1);
                    try {
                        // Attempt URL decoding for better value representation.
                        value = decodeURIComponent(value);
                    } catch (e) {
                        // Keep original value if decoding fails.
                    }
                }

                cookies.push({
                    name,
                    value,
                    domain: currentDomain,
                    purpose: classifyCookie(name, currentDomain)
                });
            });

            return cookies;
        } catch (error) {
            console.error('UCL Privacy: Error retrieving cookies:', error);
            return [];
        }
    }

    /**
     * Safely deletes a cookie using comprehensive domain and path patterns.
     * Implements conservative deletion strategy that attempts removal across
     * multiple domain and path combinations to ensure complete cleanup.
     * Uses the original cookie setter to avoid interception conflicts.
     *
     * Deletion patterns cover:
     * - Current domain with and without leading dot
     * - Parent domains for subdomain cookies
     * - Root path and current path variations
     * - Secure and non-secure configurations
     *
     * @param {string} name - Cookie name to delete.
     * @param {string} [domain] - Specific domain for targeted deletion.
     * @returns {Promise<boolean>} Promise resolving to deletion success status.
     */
    async function deleteCookie(name, domain = '') {
        if (!name || typeof name !== 'string') {
            return false;
        }

        try {
            const currentDomain = domain || window.location.hostname;
            const currentPath = window.location.pathname;

            // Generate comprehensive deletion patterns for thorough cleanup.
            const deletionPatterns = [
                // Current domain patterns.
                `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`,
                `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${currentDomain}`,
                `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${currentDomain}`,

                // Current path patterns for path-specific cookies.
                `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${currentPath}`,
                `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${currentPath}; domain=${currentDomain}`,
                `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${currentPath}; domain=.${currentDomain}`,

                // Secure cookie variants for HTTPS contexts.
                `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure`,
                `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${currentDomain}; secure`,
                `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${currentDomain}; secure`
            ];

            // Add parent domain patterns for subdomain cookie cleanup.
            const domainParts = currentDomain.split('.');
            if (domainParts.length > 2) {
                const parentDomain = domainParts.slice(1).join('.');
                deletionPatterns.push(
                    `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain}`,
                    `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${parentDomain}`
                );
            }

            // Execute deletion patterns with error isolation.
            let successCount = 0;
            for (const pattern of deletionPatterns) {
                try {
                    if (originalMethods.cookieSetter) {
                        originalMethods.cookieSetter.call(document, pattern);
                        successCount++;
                    }
                } catch (error) {
                    // Continue with other patterns on individual failures.
                    debug(`Cookie deletion pattern failed: ${pattern}`, error);
                }
            }

            const success = successCount > 0;
            if (success) {
                debug(`Cookie '${name}' deletion attempted with ${successCount}/${deletionPatterns.length} patterns`);
            } else {
                console.warn(`UCL Privacy: Failed to delete cookie '${name}' - no patterns succeeded`);
            }

            return success;
        } catch (error) {
            console.error(`UCL Privacy: Error deleting cookie '${name}':`, error);
            return false;
        }
    }

    /**
     * Performs comprehensive cookie cleanup based on consent state changes.
     * Removes cookies belonging to revoked consent categories using safe
     * deletion patterns. Implements conservative approach with comprehensive
     * error handling and detailed logging for transparency.
     *
     * Cleanup process:
     * 1. Retrieve and classify all current cookies
     * 2. Identify cookies requiring removal based on consent state
     * 3. Execute safe deletion using multiple domain/path patterns
     * 4. Log results for debugging and compliance auditing
     *
     * @param {Array<string>} [targetPurposes] - Specific purposes to clean up.
     * @returns {Promise<Object>} Cleanup results with success/failure counts.
     * @returns {number} returns.attempted - Total cookies targeted for deletion.
     * @returns {number} returns.successful - Cookies successfully deleted.
     * @returns {Array<string>} returns.errors - Names of cookies that failed deletion.
     */
    async function cleanupCookies(targetPurposes = null) {
        if (!config.options.enableCookieCleanup) {
            debug('Cookie cleanup disabled by configuration');
            return { attempted: 0, successful: 0, errors: [] };
        }

        try {
            const startTime = Date.now();
            const cookies = getCurrentCookies();
            const results = { attempted: 0, successful: 0, errors: [] };

            if (cookies.length === 0) {
                debug('No cookies found for cleanup');
                return results;
            }

            debug(`Starting cookie cleanup for ${cookies.length} cookies`);

            // Filter cookies requiring cleanup based on consent state.
            const cookiesToDelete = cookies.filter(cookie => {
                // Never delete necessary cookies.
                if (cookie.purpose === 'necessary') return false;

                // Target specific purposes if provided.
                if (targetPurposes && !targetPurposes.includes(cookie.purpose)) return false;

                // Delete if consent not granted for this purpose.
                return !consentState[cookie.purpose];
            });

            results.attempted = cookiesToDelete.length;

            if (cookiesToDelete.length === 0) {
                debug('No cookies require cleanup based on current consent state');
                return results;
            }

            // Execute deletions with individual error handling.
            const deletionPromises = cookiesToDelete.map(async cookie => {
                try {
                    const deleted = await deleteCookie(cookie.name, cookie.domain);
                    if (deleted) {
                        results.successful++;
                        debug(`Deleted ${cookie.purpose} cookie: ${cookie.name}`);
                    } else {
                        results.errors.push(cookie.name);
                        debug(`Failed to delete ${cookie.purpose} cookie: ${cookie.name}`);
                    }
                } catch (error) {
                    results.errors.push(cookie.name);
                    debug(`Error deleting cookie ${cookie.name}:`, error);
                }
            });

            // Wait for all deletions to complete.
            await Promise.all(deletionPromises);

            const duration = Date.now() - startTime;
            const successRate = results.attempted > 0 ? (results.successful / results.attempted * 100).toFixed(1) : 0;

            log(`Cookie cleanup completed: ${results.successful}/${results.attempted} deleted (${successRate}%) in ${duration}ms`);

            // Dispatch cleanup event for UI updates and external integrations.
            window.dispatchEvent(new CustomEvent('ucl-privacy-cookies-cleaned', {
                detail: {
                    results,
                    duration,
                    targetPurposes: targetPurposes || Object.keys(consentState).filter(p => !consentState[p])
                }
            }));

            return results;
        } catch (error) {
            console.error('UCL Privacy: Cookie cleanup failed:', error);
            return { attempted: 0, successful: 0, errors: ['cleanup-failed'] };
        }
    }

    /**
     * UCLPrivacy global API for consent management and system integration.
     * Provides comprehensive interface for UI components, debugging tools, and
     * external systems to interact with the consent manager whilst maintaining
     * secure boundaries and proper validation of all operations.
     *
     * Core functionality:
     * - Consent state management with atomic operations and error handling
     * - Content restoration and blocking statistics
     * - Configuration access for debugging and transparency
     * - Performance cache management and cleanup utilities
     * - Cookie cleanup and classification for consent compliance
     *
     * All state modifications are validated, logged, and persisted with proper
     * error recovery. API methods are designed for external use and include
     * comprehensive parameter validation and documentation.
     *
     * @namespace UCLPrivacy
     * @global
     */
    window.UCLPrivacy = {
        /**
         * Retrieves current consent preferences as immutable copy.
         * Returns a defensive copy to prevent external modification of internal
         * consent state. Safe for use by UI components and external integrations.
         *
         * @returns {object} Immutable copy of current consent state.
         * @returns {boolean} returns.necessary - Essential functionality (always true).
         * @returns {boolean} returns.analytics - Analytics and performance tracking consent.
         * @returns {boolean} returns.marketing - Advertising and marketing consent.
         * @returns {boolean} returns.embeds - Third-party embedded content consent.
         * @example
         * const consent = UCLPrivacy.getConsent();
         * console.log(consent.analytics); // true/false
         */
        getConsent: () => ({ ...consentState }),

        /**
         * CONSISTENCY FIX: Updates consent for specific purpose category with validation.
         * @param {string} purpose - Purpose category to update.
         * @param {boolean} allowed - Whether to grant consent for this purpose.
         * @returns {Promise<boolean>} Promise resolving to success status.
         */
        updateConsent: async (purpose, allowed) => {
            // Input validation.
            if (typeof purpose !== 'string' || typeof allowed !== 'boolean') {
                console.error('UCL Privacy: Invalid parameters for updateConsent:', { purpose, allowed });
                return false;
            }

            if (purpose in consentState && purpose !== 'necessary') {
                // Validate the change would actually modify state.
                if (consentState[purpose] === allowed) {
                    debug(`Consent for ${purpose} already set to ${allowed}, skipping update`);
                    return true;
                }

                const previousValue = consentState[purpose];
                consentState[purpose] = allowed;

                try {
                    const success = await saveUserConsent();
                    if (success) {
                        log(`Updated consent for ${purpose}: ${previousValue} → ${allowed}`);

                        // Perform cookie cleanup if consent was revoked.
                        if (!allowed && config.options.enableCookieCleanup) {
                            try {
                                debug(`Performing cookie cleanup for revoked ${purpose} consent`);
                                const cleanupResult = await cleanupCookies([purpose]);
                                if (cleanupResult.successful > 0) {
                                    log(`Cookie cleanup: deleted ${cleanupResult.successful} ${purpose} cookies`);
                                }
                            } catch (cleanupError) {
                                // Don't fail consent update if cookie cleanup fails.
                                console.warn(`UCL Privacy: Cookie cleanup failed for ${purpose}:`, cleanupError);
                            }
                        }

                        // Update consent state tracking for smart processing.
                        lastConsentState = JSON.stringify(consentState);
                        return true;
                    } else {
                        // Rollback on failure.
                        consentState[purpose] = previousValue;
                        console.error(`UCL Privacy: Failed to save consent update for ${purpose}`);
                        return false;
                    }
                } catch (error) {
                    // Rollback on error.
                    consentState[purpose] = previousValue;
                    console.error('UCL Privacy: Error updating consent:', error);
                    return false;
                }
            } else {
                console.error(`UCL Privacy: Invalid purpose category: ${purpose}`);
                return false;
            }
        },

        /**
         * CONSISTENCY FIX: Batch updates all consent preferences with atomic validation.
         * @param {object} state - Complete consent state with purpose categories.
         * @returns {Promise<boolean>} Promise resolving to success status.
         */
        updateAllConsent: async (state) => {
            // Input validation
            if (!state || typeof state !== 'object') {
                console.error('UCL Privacy: Invalid state object for updateAllConsent:', state);
                return false;
            }

            // Create backup of current state for rollback.
            const backupState = { ...consentState };
            let hasChanges = false;
            const revokedPurposes = [];

            try {
                // Apply all changes to memory first and track revoked purposes.
                Object.entries(state).forEach(([purpose, allowed]) => {
                    if (purpose in consentState && purpose !== 'necessary') {
                        const booleanAllowed = !!allowed;
                        if (consentState[purpose] !== booleanAllowed) {
                            // Track purposes being revoked for cookie cleanup.
                            if (consentState[purpose] && !booleanAllowed) {
                                revokedPurposes.push(purpose);
                            }
                            consentState[purpose] = booleanAllowed;
                            hasChanges = true;
                        }
                    }
                });

                // Always save if user hasn't consented yet, even if values match defaults.
                // This ensures the user's explicit choice is recorded and banner is dismissed.
                if (!hasChanges && hasConsented) {
                    debug('No consent changes detected, skipping save operation');
                    return true;
                }

                // Attempt atomic save.
                const success = await saveUserConsent();
                if (success) {
                    log('Updated all consent preferences:', consentState);

                    // Perform cookie cleanup for revoked purposes.
                    if (revokedPurposes.length > 0 && config.options.enableCookieCleanup) {
                        try {
                            debug(`Performing cookie cleanup for revoked purposes: ${revokedPurposes.join(', ')}`);
                            const cleanupResult = await cleanupCookies(revokedPurposes);
                            if (cleanupResult.successful > 0) {
                                log(`Cookie cleanup: deleted ${cleanupResult.successful} cookies from revoked purposes`);
                            }
                        } catch (cleanupError) {
                            // Don't fail consent update if cookie cleanup fails.
                            console.warn('UCL Privacy: Cookie cleanup failed during batch update:', cleanupError);
                        }
                    }

                    // Update consent state tracking for smart processing.
                    lastConsentState = JSON.stringify(consentState);
                    return true;
                } else {
                    // Rollback all changes on save failure.
                    Object.assign(consentState, backupState);
                    console.error('UCL Privacy: Failed to save batch consent update, rolled back changes');
                    return false;
                }
            } catch (error) {
                // Rollback all changes on error.
                Object.assign(consentState, backupState);
                console.error('UCL Privacy: Error during batch consent update, rolled back changes:', error);
                return false;
            }
        },

        /**
         * Retrieves comprehensive blocking statistics for monitoring and debugging.
         * Provides detailed metrics on blocked resources organised by type and purpose
         * category. Essential for compliance auditing, performance analysis, and
         * troubleshooting consent manager effectiveness.
         *
         * @returns {object} Complete blocking statistics with nested structure.
         * @returns {object} returns.blocked - Total blocked resources by type.
         * @returns {number} returns.blocked.scripts - Total blocked script elements.
         * @returns {number} returns.blocked.requests - Total blocked network requests.
         * @returns {number} returns.blocked.cookies - Total blocked cookie operations.
         * @returns {number} returns.blocked.iframes - Total blocked iframe elements.
         * @returns {object} returns.byPurpose - Statistics organised by consent category.
         * @example
         * const stats = UCLPrivacy.getStats();
         * console.log(`Blocked ${stats.blocked.scripts} scripts total`);
         * console.log(`Blocked ${stats.byPurpose.marketing.requests} marketing requests`);
         */
        getStats: () => ({ ...stats }),

        /**
         * Determines if user has provided explicit consent preferences.
         * Returns true when user has interacted with consent interface and made
         * explicit choices about data processing. Used by UI components to determine
         * whether to show consent banner or persistent settings access.
         *
         * @returns {boolean} True if user has made explicit consent decisions.
         * @example
         * if (!UCLPrivacy.hasUserConsented()) {
         *   showConsentBanner();
         * }
         */
        hasUserConsented: () => hasConsented,

        /**
         * Triggers content restoration for specified purpose category.
         * Updates consent state and dispatches restoration events to UI layer.
         * Handles both new consent grants and manual restoration triggers for
         * already-consented categories. Used by placeholder buttons and settings.
         *
         * If consent is not already granted, updates consent state through proper
         * validation and persistence. If consent exists, dispatches manual restoration
         * event to ensure UI synchronisation and content activation.
         *
         * @param {string} purpose - Purpose category to restore: 'analytics', 'marketing', or 'embeds'.
         * @returns {void} Updates consent state and dispatches restoration events.
         * @example
         * // User clicks 'Accept Analytics' button
         * UCLPrivacy.restoreContent('analytics');
         */
        restoreContent: (purpose) => {
            if (!consentState[purpose]) {
                UCLPrivacy.updateConsent(purpose, true);
            } else {
                /*
                 * Manual event dispatch ensures UI updates even when consent unchanged.
                 * Critical for placeholder restoration when user clicks 'Accept' on
                 * already-consented categories.
                 */
                window.dispatchEvent(new CustomEvent('ucl-privacy-consent-updated', { detail: { state: consentState } }));
            }
            log(`Restoration triggered for ${purpose} content`);
        },

        /**
         * Provides real-time count of blocked DOM elements.
         * Performs live DOM queries to count currently blocked elements by type.
         * Useful for debugging, compliance monitoring, and UI status indicators.
         * Results reflect current DOM state and may change as content is restored.
         *
         * @returns {object} Current blocked element counts by type.
         * @returns {number} returns.scripts - Number of script elements currently blocked.
         * @returns {number} returns.iframes - Number of iframe elements currently blocked.
         * @example
         * const blocked = UCLPrivacy.getBlockedElements();
         * console.log(`${blocked.scripts} scripts and ${blocked.iframes} iframes blocked`);
         */
        getBlockedElements: () => {
            return {
                scripts: document.querySelectorAll('.privacy-blocked').length,
                iframes: document.querySelectorAll('iframe[data-blocked-purpose]').length
            };
        },

        /**
         * Returns current cookie classification patterns for debugging and transparency.
         * Provides insight into both built-in and user-extended cookie classification
         * rules for troubleshooting consent decisions and pattern effectiveness.
         *
         * Pattern objects contain compiled RegExp instances for performance,
         * suitable for direct testing against cookie names during development.
         *
         * @returns {object} Cookie patterns by purpose category with compiled RegExp objects.
         */
        getCookiePatterns: () => ({ ...config.cookiePatterns }),

        /**
         * Returns current proxy detection patterns for debugging and security auditing.
         * Enables verification of oEmbed and CMS proxy detection rules to ensure
         * comprehensive coverage of consent circumvention attempts through proxying.
         *
         * These patterns identify URL paths that suggest first-party domains are
         * serving third-party content, requiring consent classification of the
         * actual destination rather than the proxy domain.
         *
         * @returns {array} Array of proxy endpoint patterns for security analysis.
         */
        getProxyPatterns: () => [...proxyPatterns],

        /**
         * Returns user-configured platform mapping extensions for service name resolution.
         * Provides access to custom hostname-to-display-name mappings configured by
         * site operators for enhanced user experience in consent interfaces.
         *
         * Used by UI components to show user-friendly service names instead of
         * technical hostnames in consent placeholders and modal descriptions.
         *
         * @returns {object} User-configured platform mapping extensions only.
         */
        getPlatformMapping: () => ({ ...userConfig.platformMapping }),

        /**
         * Configuration object for debugging and transparency.
         * Provides read-only access to the complete merged configuration including
         * user customisations, domain lists, patterns, and operational settings.
         * Essential for troubleshooting classification issues and verifying setup.
         *
         * @type {object}
         * @readonly
         */
        config: config,

        /**
         * Current version of the UCL consent blocker for compatibility checking.
         * Injected during build process from package.json version. Used by UI
         * components and external integrations to verify API compatibility.
         *
         * @type {string}
         * @readonly
         * @example
         * console.log(`UCL Privacy version: ${UCLPrivacy.version}`);
         */
        version: __VERSION__,

        /**
         * Clears all performance caches and rebuilds optimisation structures.
         * Useful when configuration changes dynamically or for memory management
         * in long-running applications. Should be used sparingly as it resets all
         * performance optimisations and may cause temporary processing delays.
         *
         * Cleared caches:
         * - URL classification cache
         * - Allowlist pattern cache
         * - Domain lookup hash map
         * - Processed elements tracking
         * - Consent state tracking
         *
         * Automatically rebuilds domain lookup map after clearing for continued
         * performance. Useful after configuration updates or memory pressure.
         *
         * @returns {void} Clears caches and rebuilds core data structures.
         * @example
         * // After dynamic configuration change
         * updatePrivacyConfig(newConfig);
         * UCLPrivacy.clearCaches();
         */
        clearCaches: () => {
            urlPatternCache.clear();
            allowlistCache.clear();
            domainLookupMap.clear();
            processedElements = new WeakSet();
            lastConsentState = JSON.stringify(consentState);

            // Rebuild domain lookup map after clearing
            buildDomainLookupMap();

            if (config.options.debug) {
                debug('Performance caches cleared and rebuilt');
            }
        },

        /**
         * Retrieves and classifies all cookies from the current domain.
         * Provides comprehensive cookie analysis including classification by
         * consent category and metadata for transparency and debugging.
         * Uses safe parsing with error recovery for malformed cookie strings.
         *
         * @returns {Array<Object>} Array of classified cookie objects.
         * @returns {string} returns[].name - Cookie name.
         * @returns {string} returns[].value - Cookie value (URL decoded if possible).
         * @returns {string} returns[].domain - Current domain context.
         * @returns {string} returns[].purpose - Classified consent category.
         * @example
         * const cookies = UCLPrivacy.getCookies();
         * cookies.forEach(c => console.log(`${c.name}: ${c.purpose}`));
         */
        getCookies: () => getCurrentCookies(),

        /**
         * Classifies a cookie name by consent category using built-in patterns.
         * Provides transparency into classification decisions for debugging
         * and compliance verification. Uses conservative classification that
         * defaults to marketing for unknown patterns.
         *
         * @param {string} cookieName - Name of the cookie to classify.
         * @param {string} [domain] - Domain context for enhanced classification.
         * @returns {string} Classification: 'necessary', 'analytics', 'marketing', 'embeds'.
         * @example
         * const purpose = UCLPrivacy.classifyCookie('_ga');
         * console.log(purpose); // 'analytics'
         */
        classifyCookie: (cookieName, domain) => classifyCookie(cookieName, domain),

        /**
         * Performs comprehensive cookie cleanup for revoked consent categories.
         * Removes cookies belonging to non-consented purposes using safe deletion
         * patterns across multiple domain and path combinations. Provides detailed
         * results for transparency and compliance auditing.
         *
         * Cleanup is automatically disabled if enableCookieCleanup option is false.
         * Uses conservative approach with comprehensive error handling to prevent
         * disruption of essential functionality.
         *
         * @param {Array<string>} [targetPurposes] - Specific purposes to clean up.
         * @returns {Promise<Object>} Cleanup results with detailed metrics.
         * @returns {number} returns.attempted - Total cookies targeted for deletion.
         * @returns {number} returns.successful - Cookies successfully deleted.
         * @returns {Array<string>} returns.errors - Names of cookies that failed deletion.
         * @example
         * // Clean up all non-consented cookies
         * const result = await UCLPrivacy.cleanupCookies();
         * console.log(`Deleted ${result.successful}/${result.attempted} cookies`);
         *
         * // Clean up only marketing cookies
         * const marketingResult = await UCLPrivacy.cleanupCookies(['marketing']);
         */
        cleanupCookies: (targetPurposes) => cleanupCookies(targetPurposes),

        /**
         * Safely deletes a specific cookie using comprehensive deletion patterns.
         * Attempts removal across multiple domain and path combinations to ensure
         * complete cleanup. Uses original cookie setter to avoid interception conflicts.
         *
         * @param {string} cookieName - Name of the cookie to delete.
         * @param {string} [domain] - Specific domain for targeted deletion.
         * @returns {Promise<boolean>} Promise resolving to deletion success status.
         * @example
         * // Delete specific cookie
         * const deleted = await UCLPrivacy.deleteCookie('_ga');
         * console.log(deleted ? 'Cookie deleted' : 'Deletion failed');
         */
        deleteCookie: (cookieName, domain) => deleteCookie(cookieName, domain),

        /**
         * Cleanly dismantles consent manager and restores native browser APIs.
         * Completely removes all consent enforcement by restoring original browser
         * methods and clearing all caches and observers. Intended exclusively for
         * testing, debugging, and development scenarios.
         *
         * **WARNING**: Should never be used in production environments where GDPR
         * compliance is required, as it completely disables privacy protection.
         *
         * Cleanup operations:
         * - Disconnects MutationObserver
         * - Restores original XMLHttpRequest.prototype.open
         * - Restores original window.fetch
         * - Restores original document.createElement
         * - Restores original document.cookie descriptor
         * - Clears all performance caches
         *
         * @returns {void} Completely disables consent manager functionality.
         * @example
         * // Testing scenario only
         * UCLPrivacy.cleanup();
         */
        cleanup: () => {
            observer.disconnect();

            // Clear all performance caches
            urlPatternCache.clear();
            allowlistCache.clear();
            domainLookupMap.clear();

            if (originalMethods.xmlHttpRequestOpen) XMLHttpRequest.prototype.open = originalMethods.xmlHttpRequestOpen;
            if (originalMethods.fetch) window.fetch = originalMethods.fetch;
            if (originalMethods.createElement) document.createElement = originalMethods.createElement;
            if (originalMethods.cookieSetter) Object.defineProperty(document, 'cookie', { set: originalMethods.cookieSetter, get: originalMethods.cookieGetter });
            log('UCL Privacy Protection cleanup complete');
        }
    };

    log('UCL Privacy Blocker initialised', { mode: config.options.blockingMode, consent: consentState });
})();
